import { getDeviceTimeZone } from '@electron/shared/device-timezone';
import { IPCEventType } from '@electron/shared/ipc.types';
import { LbNotificationExpiry } from '@electron/shared/lb-notification.types';
import {
  AppDetectionAttemptFieldsSchema,
  type EventType,
  getMeetingLink,
  getMeetingProvider,
  getMeetingProviders,
  getMeetingRoomFromLink,
  getProviderFromBundleId,
  isDeclinedByUser,
  isSoloEvent,
  startTriggerFor,
  type MeetingRecoverablePostProcessingRecordingsPayload,
  type StopNoteTakingInactivityTimeoutTelemetry,
  type StopNoteTakingPresenceLostTelemetry,
  type StopNoteTakingTelemetry,
  type TranscriptionEngine,
} from '@electron/shared/meetings.types';
import { HttpError } from '@littlebird/auth-client';
import {
  config,
  hasSDK,
  getSDK,
  getStores,
  type Meeting as SDKMeeting,
  MeetingCreationCancelledError,
  MeetingQuotaExceededError,
  buildMeetingSearchItems,
  getMeetingSearchDocIds,
  extractBackendErrorCode,
  isMeetingBlocked,
} from '@littlebird/sdk';
import { buildMeetingCircles, hashString } from '@littlebird/ui-utils';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { reaction, runInAction, toJS, when } from 'mobx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { type z } from 'zod';

import { backendApis } from '@/backendApis';
import { isEmptyContent } from '@/components/ui/editor';
import { isDebug } from '@/env';
import { coachMarks } from '@/features/coach-marks';
import { contextChatStore } from '@/features/context-chat/context-chat.state';
import { NativeStartRejectedError } from '@/features/electron-api/features/NativeStartRejectedError';
import { meetingDebugStore } from '@/features/meeting-debug/meeting-debug.store';
import { meetingSaves } from '@/features/meetings/meeting-save-state';
import { subscriptionApis } from '@/features/subscription/subscription.query';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { logError } from '@/lib/errorLogging';
import { resolvePreferredTranscriptionEngine } from '@/lib/meetings/transcription-engine';
import { queryClient } from '@/query/client';
import { settingsStore } from '@/stores/settings.store';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import { AppCapability, inBrowser, isCapable } from '@/utils';
import { trackEvent } from '@/utils/analytics';
import { navigate, preloadRoute } from '@/utils/navigation-service';
import { errorToast } from '@/utils/toast';
import { assertNever } from '@/utils/types';

import { electronApis } from '../electron-api';

import { getLocalTranscriptionSyncTraceSink } from './local-transcription-debug';
import {
  meetingAppDetections,
  withdrawMeetingAppOffer,
} from './meeting-app-detections';
import {
  readCachedMeetingCircles,
  refreshMeetingCirclesIfChanged,
  writeCachedMeetingCircles,
} from './meeting-circles-cache';
import {
  convertDesktopAttendeesToStoreAttendees,
  resolveMeetingRecordingStoppedSummaryDisposition,
  storeMeetingToMeeting,
} from './meeting-mapper';
import { GENERATE_SUMMARY_COACH_MARK } from './meetings-coach-marks';
import {
  getMeetingSummaryNotificationId,
  notifyDeferredSummaryReady,
  notifySummaryReady,
} from './meetings-completion-notices';
import { logIfContextKitRefusedStartWhileAppIdle } from './meetings-native-start-refusal';
import {
  noteRecordingStarted,
  noteRecordingStopped,
} from './meetings-recording-continuity';
import { createRemoteTranscriptDeltaNormalizer } from './meetings-transcript-delta-normalizer';
import { getAttendeeDisplayName } from './meetings-transcript-display.hooks';
import {
  alertUnexpectedRecordingStop,
  clearUnexpectedStop,
  rememberRecordingMeeting,
} from './meetings-unexpected-stop';
import { isUtteranceDelta } from './meetings-utterance-projection';
import { createUtteranceDeltaSink } from './meetings-utterance-sync-gate';
import { ppDebug } from './meetings.debug-log';
import { type InterruptedRecordingRecord } from './meetings.durable-map';
import {
  reconcilePostProcessingJobs,
  resolveParkedRoundBeforeResume,
  setOnPostProcessingFinalized,
  shouldUseLocalPostProcessing,
  startLocalPostProcessing,
} from './meetings.post-processing';
import { buildRoundLedgerFields } from './meetings.processing-lifecycle';
import {
  keepRecordingSessionAlive,
  type RecordingSession,
} from './meetings.recording-session';
import {
  armInterruptedPromotionTimers,
  interruptedPromotionTimers,
  performPromoteInterruptedTransition,
  performRecoverInterruptedTransition,
  hasTextAfterSyncedLength,
  retryInterruptedTranscript,
  setLiveSpeechUnsynced,
  setPromoteInterruptedEnqueue,
} from './meetings.recovery';
import {
  getMeeting,
  getMeetingByEventId,
  meetingStore,
} from './meetings.state';
import {
  type RecordingState,
  type Meeting,
  type MeetingAudioEnergyLevel,
  type MeetingAudioEnergyLevelNative,
  type MeetingId,
  type MeetingWindowFocusChange,
  type MeetingPermissionType,
  type MeetingPermissions,
  type CalendarEvent,
  type StartMeetingInput,
  type MeetingProvider,
} from './meetings.types';
import {
  getOrCreateManager,
  isTransientTranscriptSyncError,
  removeManager,
  type TranscriptSyncManager,
} from './transcript-sync';

/**
 * Internal stop payload — a discriminated union so variants like `device_conflict`
 * can carry extra data. `.type` is always a valid `StopNoteTakingReason` for IPC.
 */
type StopMeetingPayload =
  | { type: 'stop_note_taking' }
  | {
      type: 'inactivity_timeout';
      telemetry: StopNoteTakingInactivityTimeoutTelemetry;
    }
  | {
      type: 'meeting_presence_lost';
      telemetry?: StopNoteTakingPresenceLostTelemetry;
    }
  | { type: 'sync_failure' }
  | { type: 'backend_create_failed' }
  | { type: 'meeting_limit_reached' }
  | { type: 'start_error' }
  | { type: 'contextkit-crash' }
  | { type: 'transcriber_interrupt' }
  | { type: 'audio_start_failed' }
  | { type: 'ipc_timeout' }
  | { type: 'new_meeting_started' };

function stopTelemetryForReason(
  reason: StopMeetingPayload
): StopNoteTakingTelemetry | undefined {
  switch (reason.type) {
    case 'inactivity_timeout':
      return { inactivityTimeoutTelemetry: reason.telemetry };
    case 'meeting_presence_lost':
      return reason.telemetry
        ? { presenceLostTelemetry: reason.telemetry }
        : undefined;
    default:
      return undefined;
  }
}

type MeetingTransition =
  | {
      type: 'start';
      payload: StartMeetingInput;
      onMeetingStarted?: (meetingId: string) => void;
    }
  | {
      type: 'stop';
      reason?: StopMeetingPayload;
      options?: StopMeetingOptions;
    }
  | {
      type: 'recoverInterrupted';
      recordings: MeetingRecoverablePostProcessingRecordingsPayload['recordings'];
    }
  | {
      type: 'promoteInterrupted';
      meetingId: string;
    };

interface StopMeetingResult {
  meetingId: string;
  meetingWasPersistedInBackend: boolean;
}

interface StopMeetingOptions {
  /** Called after stop flow completes */
  onStopped?: (result: StopMeetingResult) => void;
  deferSummary?: boolean;
}

type SummaryStartMethod = 'auto' | 'generate_button' | 'manual';

const AUTO_SUMMARIZE_WORD_THRESHOLD = 500;
const STOP_TRANSCRIPT_FLUSH_RETRY_BUDGET_MS = 30_000;
const SESSION_LOST_STOP_ALERT = {
  cause: 'lease_lost',
  title: 'Recording stopped',
  message:
    'Recording stopped because this meeting is now recording on another device, or the server ended it. Resume to keep recording here.',
} as const;

class MeetingTransitionQueue {
  private queue: Array<{
    transition: MeetingTransition;
    resolve: () => void;
    reject: (error: unknown) => void;
  }> = [];
  private processing = false;

  constructor(private processor: (t: MeetingTransition) => Promise<void>) {}

  enqueue(transition: MeetingTransition): Promise<void> {
    return new Promise((resolve, reject) => {
      this.queue.push({ transition, resolve, reject });
      if (!this.processing) this.processNext();
    });
  }

  private async processNext(): Promise<void> {
    if (this.processing) return;
    const entry = this.queue.shift();
    if (!entry) return;
    this.processing = true;
    try {
      await this.processor(entry.transition);
      entry.resolve();
    } catch (error) {
      entry.reject(error);
    } finally {
      this.processing = false;
      this.processNext();
    }
  }
}

function trackMeetingStart(
  meetingId: string,
  payload: StartMeetingInput,
  eventId: string | undefined
) {
  const resumeSource =
    payload.startReason === 'resume' ? payload.resumeSource : undefined;
  const startMethod = resumeSource
    ? 'resume'
    : payload.startReason === 'app_detection'
      ? 'auto_detected'
      : payload.startReason === 'calendar_event'
        ? 'calendar'
        : payload.startReason === 'meeting_segment'
          ? 'meeting_segment_switch'
          : 'manual';

  trackEvent('MEETING_RECORDING_STARTED', {
    meeting_id: meetingId,
    start_method: startMethod,
    resume_source: resumeSource,
    calendar_event_id_hash: hashCalendarEventId(eventId),
    ...noteRecordingStarted(
      meetingId,
      meetingStore.recordingSessionAppBundleId
    ),
  });
}

function hashCalendarEventId(eventId: string | null | undefined) {
  return eventId ? hashString(eventId) : undefined;
}

function hasMeetingLink(event: CalendarEvent | undefined) {
  return !!event && !!getMeetingLink(event);
}

function startExpectsCall(
  payload: StartMeetingInput,
  startEvent: CalendarEvent | undefined
) {
  return match(payload)
    .with(
      { startReason: 'calendar_event' },
      (p) => !!p.provider || hasMeetingLink(startEvent)
    )
    .with({ startReason: 'resume' }, (p) => {
      const resumedEventId = getMeeting(p.meetingId)?.event_id;
      return (
        !!resumedEventId &&
        hasMeetingLink(findCalendarEventById(resumedEventId))
      );
    })
    .otherwise(() => false);
}

function getInitialSessionAppBundleId(
  payload: StartMeetingInput
): string | undefined {
  if (payload.startReason === 'resume') {
    return getMeeting(payload.meetingId)?.source_app ?? undefined;
  }

  if ('appBundleId' in payload) {
    return payload.appBundleId;
  }

  return undefined;
}

function isAutoStart(
  payload: StartMeetingInput
): payload is Extract<StartMeetingInput, { startReason: 'app_detection' }> {
  return (
    payload.startReason === 'app_detection' &&
    (payload.startAction === 'timer_auto_start' ||
      payload.startAction === 'auto_start_without_notification')
  );
}

function getAutoStartRejection(payload: StartMeetingInput) {
  if (!isAutoStart(payload)) return undefined;
  if (meetingStore.recording.status !== 'idle') return 'already_recording';
  if (!meetingAppDetections.isAutoStartLive(payload.autoStartAttemptId)) {
    return 'auto_start_cancelled';
  }
  return undefined;
}

/** The recording this start replaces, for the two start reasons that switch
 * away from a running one rather than beginning from idle. */
function getPrecedingRecordingSwitch(payload: StartMeetingInput): {
  precedingMeetingId: string | undefined;
  precedingRecordingStopReason: 'new_meeting_started' | undefined;
} {
  if (
    payload.startReason !== 'calendar_event' &&
    payload.startReason !== 'meeting_segment'
  ) {
    return {
      precedingMeetingId: undefined,
      precedingRecordingStopReason: undefined,
    };
  }

  return {
    precedingMeetingId: payload.precedingMeetingId,
    precedingRecordingStopReason: payload.precedingRecordingStopReason,
  };
}

function logStartTransitionResult({
  payload,
  outcome,
  meetingId,
  appBundleId,
  error,
  failureReason,
  transcriptionEngine,
}: {
  payload: StartMeetingInput;
  outcome: 'success' | 'failure';
  meetingId?: string;
  appBundleId?: string;
  error?: Error;
  failureReason?: string;
  transcriptionEngine?: TranscriptionEngine;
}) {
  // eslint-disable-next-line no-console -- PostHog captures renderer console.log breadcrumbs
  console.log('[meeting-auto-start] start_transition_result', {
    outcome,
    meeting_id: meetingId ?? null,
    start_reason: payload.startReason,
    app_bundle_id: appBundleId ?? null,
    ...(payload.startReason === 'app_detection' && {
      start_action: payload.startAction ?? null,
      detection_id: payload.detectionId ?? null,
      auto_start_attempt_id: payload.autoStartAttemptId ?? null,
      notification_id: payload.notificationId ?? null,
    }),
    error_name: error?.name ?? null,
    failure_reason: failureReason ?? null,
  });
  meetingDebugStore.record({
    kind: 'start_result',
    meetingId: meetingId ?? null,
    detectionId:
      payload.startReason === 'app_detection' ? payload.detectionId : undefined,
    autoStartAttemptId:
      payload.startReason === 'app_detection'
        ? payload.autoStartAttemptId
        : undefined,
    summary:
      outcome === 'success'
        ? `Started (${payload.startReason})`
        : `Start failed (${payload.startReason}): ${failureReason ?? error?.message ?? 'unknown error'}`,
    fields: {
      outcome,
      startReason: payload.startReason,
      appBundleId,
      transcriptionEngine,
      failureReason,
      errorName: error?.name,
      errorMessage: error?.message,
    },
  });
}

const POLLING_INTERVAL_MS = 500;
const UPDATE_DEBOUNCE_MS = 250;
const CALENDAR_REFETCH_INTERVAL = 60_000;

function updateStoreMeeting(id: string, data: Partial<Meeting>): void {
  const existingMeeting = getMeeting(id);
  const updatedMeeting: Meeting = {
    ...(existingMeeting ?? {
      id,
      created_at: new Date().toISOString(),
      updated_at: undefined,
      name: null,
      summary: null,
      tldr: null,
      personified_transcript: null,
      notes: null,
      prep: null,
      source_app: null,
      event_id: null,
      status: 'active',
      summary_status: null,
      attendees: null,
      speakers: null,
      ended_at: null,
      first_chunk_timestamp: null,
    }),
    ...data,
  };

  meetingStore.setMeeting(updatedMeeting);

  if (!hasSDK()) return;
  const store = getStores().meetings;
  const storeMeeting = store.get(id);

  if (!storeMeeting) {
    store.add({
      id: updatedMeeting.id,
      name: updatedMeeting.name ?? null,
      summary: updatedMeeting.summary ?? null,
      tldr: updatedMeeting.tldr ?? null,
      personifiedTranscript: updatedMeeting.personified_transcript ?? null,
      notes: updatedMeeting.notes ?? null,
      prep: updatedMeeting.prep ?? null,
      sourceApp: updatedMeeting.source_app ?? null,
      externalSource: updatedMeeting.external_source ?? null,
      eventId: updatedMeeting.event_id ?? null,
      projectId: updatedMeeting.project_id ?? null,
      status: updatedMeeting.status ?? 'active',
      summaryStatus: updatedMeeting.summary_status ?? 'initial',
      attendees: convertDesktopAttendeesToStoreAttendees(
        updatedMeeting.attendees
      ),
      speakers: updatedMeeting.speakers ?? null,
      endedAt: updatedMeeting.ended_at
        ? new Date(updatedMeeting.ended_at)
        : null,
      firstChunkTimestamp: updatedMeeting.first_chunk_timestamp
        ? new Date(updatedMeeting.first_chunk_timestamp)
        : null,
      createdAt: updatedMeeting.created_at
        ? new Date(updatedMeeting.created_at)
        : new Date(),
      updatedAt: updatedMeeting.updated_at
        ? new Date(updatedMeeting.updated_at)
        : undefined,
    });
    return;
  }

  if (data.name !== undefined) storeMeeting.name = data.name;
  if (data.summary !== undefined) storeMeeting.summary = data.summary;
  if (data.tldr !== undefined) storeMeeting.tldr = data.tldr;
  if (data.personified_transcript !== undefined)
    storeMeeting.personifiedTranscript = data.personified_transcript;
  if (data.notes !== undefined) storeMeeting.notes = data.notes;
  if (data.prep !== undefined) storeMeeting.prep = data.prep;
  if (data.project_id !== undefined) storeMeeting.projectId = data.project_id;
  if (data.external_source !== undefined)
    storeMeeting.externalSource = data.external_source;
  if (data.status !== undefined) storeMeeting.status = data.status;
  if (data.summary_status !== undefined && data.summary_status !== null)
    storeMeeting.summaryStatus = data.summary_status;
  if (data.attendees !== undefined)
    storeMeeting.attendees = convertDesktopAttendeesToStoreAttendees(
      data.attendees
    );
  if (data.speakers !== undefined) storeMeeting.speakers = data.speakers;
}

async function getBackendTranscriptForResume(
  meetingId: MeetingId
): Promise<{ transcript: string; currentOffset: number }> {
  const transcriptState =
    await getStores().meetings.fetchMeetingTranscript(meetingId);
  return {
    transcript: transcriptState.personifiedTranscript ?? '',
    currentOffset: transcriptState.currentLength,
  };
}

interface CleanupCandidate {
  id: string;
  name: string | null;
  summary: string | null;
  personifiedTranscript: string | null;
  summaryStatus: string;
  notes: Parameters<typeof isEmptyContent>[0];
}

export function getEmptyMeetingIds(
  meetings: CleanupCandidate[],
  recordingId?: string
): string[] {
  return meetings
    .filter((m) => {
      if (m.id === recordingId) return false;
      if (m.name) return false;
      if (m.summary) return false;
      if (m.personifiedTranscript) return false;
      if (!['completed', 'no_transcript'].includes(m.summaryStatus))
        return false;
      if (!isEmptyContent(m.notes)) return false;
      return true;
    })
    .map((m) => m.id);
}

export const isBlockedByTeamRecordingRules = async (
  event: Pick<CalendarEvent, 'summary' | 'attendees'>
) => {
  // Only teams have rules, so nobody else waits on a request before recording
  if (!subscriptionStore.isTeamMember) return false;
  const rules = await backendApis.meetingNotes
    .fetchRecordingRules()
    .catch(() => []);
  const attendeeEmails =
    event.attendees?.flatMap((attendee) =>
      attendee.email ? [attendee.email] : []
    ) ?? [];
  return isMeetingBlocked(rules, event.summary, attendeeEmails);
};

export async function ensurePrepMeetingForCalendarEvent(
  event: Pick<CalendarEvent, 'id' | 'summary'>,
  existingMeeting?: Pick<Meeting, 'id'>,
  options?: { onError?: () => void }
): Promise<string> {
  if (existingMeeting) return existingMeeting.id;

  const { meetings } = getStores();

  let recordingDeviceId: string | undefined;
  let recordingPlatform: string | undefined;
  try {
    recordingDeviceId = await electronApis.system.getDeviceId();
  } catch (error) {
    logError(
      error,
      '[prepMeeting] getDeviceId failed (best-effort, continuing)'
    );
  }
  try {
    recordingPlatform = await electronApis.system.getPlatform();
  } catch (error) {
    logError(
      error,
      '[prepMeeting] getPlatform failed (best-effort, continuing)'
    );
  }

  const meeting = await meetings.ensurePrepMeeting(event.id, event.summary, {
    recordingDeviceId,
    recordingPlatform,
    onError: (error) => {
      if (error instanceof MeetingCreationCancelledError) return;
      toast.error('Failed to create meeting note.');
      if (meetings.getMeetingByEventId(event.id)) return;
      options?.onError?.();
    },
  });
  return meeting.id;
}

function navigateToSummaryTabIfInView(meetingId: MeetingId) {
  if (meetingStore.meetingInViewId === meetingId) {
    navigate({
      to: '/meetings/$meetingId',
      params: { meetingId: meetingId.toString() },
      search: { tab: 'summary' },
      replace: true,
    });
  }
}

/** The meeting app closed, so the user is probably at work in another app.
 * The summary opens without taking the keyboard focus from it */
async function showAutomaticallyStoppedMeeting(meetingId: MeetingId) {
  const showSummary = config.meetings.show_summary_after_auto_stop ?? true;
  trackEvent('MEETING_AUTO_STOP_SUMMARY_HANDLED', {
    meeting_id: meetingId,
    outcome: showSummary ? 'shown_without_focus' : 'notification_only',
  });
  if (!showSummary) return;
  await electronApis.window
    .mainWindowNavigateTo('/meetings/$meetingId', {
      params: { meetingId },
      search: { tab: 'summary' },
      showInactive: true,
    })
    .catch((error) => {
      logError(
        error,
        '[stopMeeting] Failed to open automatically stopped meeting'
      );
      toast.error('Could not open the meeting summary.');
    });
}

function askContextKitToStopItsStartWithoutWaiting(reason: StopMeetingPayload) {
  electronApis.meetings
    .stopMeeting(reason.type, stopTelemetryForReason(reason))
    .catch((error) =>
      logError(
        error,
        '[stopMeeting] Failed to stop unacknowledged native start'
      )
    );
}

async function performStopMeetingTransition(
  reason: StopMeetingPayload = { type: 'stop_note_taking' },
  options?: StopMeetingOptions
) {
  const recordingSnapshot = meetingStore.recording;
  const sessionAppBundleId = meetingStore.recordingSessionAppBundleId;
  let transcriptSyncFailed = false;

  if (
    recordingSnapshot.status === 'stopping' ||
    recordingSnapshot.status === 'idle'
  ) {
    return;
  }

  const {
    meetingId,
    transcriptManager,
    unlistenTranscriptDelta,
    replaceWatermark,
    usesLocalPostProcessing,
  } = recordingSnapshot;
  meetingDebugStore.record({
    kind: 'stop',
    meetingId,
    summary: `Stopped: ${reason.type}`,
    fields: {
      ...reason,
      recordingStatus: recordingSnapshot.status,
      deferSummary: options?.deferSummary,
    },
  });
  const started =
    recordingSnapshot.status === 'recording' ? recordingSnapshot : null;
  const electronApiStartMeetingWasHit =
    recordingSnapshot.status === 'recording' ||
    recordingSnapshot.electronApiStartMeetingWasHit;
  const nativeStartUnacknowledged =
    recordingSnapshot.status === 'loading' &&
    recordingSnapshot.nativeStartRequested === true &&
    !recordingSnapshot.electronApiStartMeetingWasHit;
  const startedAt = started?.startedAt;
  const transcriptLengthAtStart = started?.transcriptLengthAtStart ?? 0;
  const activeTranscriptionEngine = started?.activeTranscriptionEngine;
  let enteredFinalizePath = false;
  let effectiveDeferSummary = false;
  let deferOverriddenByWordThreshold = false;

  meetingStore.setRecording({
    status: 'stopping',
    meetingId,
    recordingSession: recordingSnapshot.recordingSession,
    startedAt: startedAt ?? new Date(),
    activeTranscriptionEngine,
    usesLocalPostProcessing,
    transcriptLengthAtStart,
    replaceWatermark,
  });

  const interruptedStartFailure =
    reason.type === 'start_error' &&
    !electronApiStartMeetingWasHit &&
    meetingStore.interruptedRecordings.has(meetingId);
  const shouldFinalizeStoppedMeeting =
    reason.type !== 'contextkit-crash' && !interruptedStartFailure;
  const recoveryIntent: Omit<InterruptedRecordingRecord, 'userId'> | undefined =
    !interruptedStartFailure &&
    recordingSnapshot.recordingSession &&
    transcriptManager?.meetingExistsInBackend
      ? {
          meetingId,
          interruptedAt: new Date().toISOString(),
          reason: shouldFinalizeStoppedMeeting
            ? 'stop-pending'
            : 'contextkit-crash',
          recordingSession: recordingSnapshot.recordingSession,
          transcriptRevision:
            recordingSnapshot.recordingSession.transcriptRevision,
          usesLocalPostProcessing,
          replaceWatermark,
          pendingTranscript: transcriptManager.getPendingTranscript(),
          deferSummary: options?.deferSummary,
        }
      : undefined;
  if (recoveryIntent) meetingStore.setInterruptedRecording(recoveryIntent);
  if (nativeStartUnacknowledged && reason.type !== 'contextkit-crash') {
    askContextKitToStopItsStartWithoutWaiting(reason);
  }
  try {
    if (electronApiStartMeetingWasHit && shouldFinalizeStoppedMeeting) {
      await electronApis.meetings.stopMeeting(
        reason.type,
        stopTelemetryForReason(reason)
      );
    }
  } catch (error) {
    logError(error, '[stopMeeting] Failed to stop native recording');
    meetingStore.setRecording(recordingSnapshot);
    toast.error('Could not stop recording. Try stopping it again.');
    return;
  }
  const recordingStoppedAtMs = Date.now();
  started?.unlistenPostStartNativeEvents();
  unlistenTranscriptDelta?.();

  if (!interruptedStartFailure && transcriptManager?.meetingExistsInBackend) {
    if (
      shouldFinalizeStoppedMeeting &&
      recordingSnapshot.status === 'recording' &&
      activeTranscriptionEngine !== 'local'
    ) {
      const interimChunk = meetingStore.getInterimChunks(meetingId)[0];
      if (interimChunk) {
        transcriptManager.addDelta({
          speaker: interimChunk.speaker,
          text: interimChunk.text,
        });
      }
    }

    const stopRetrying = setTimeout(
      () => transcriptManager.stopRetrying(),
      STOP_TRANSCRIPT_FLUSH_RETRY_BUDGET_MS
    );
    try {
      await transcriptManager.keepFlushing();
    } catch (error) {
      transcriptSyncFailed = true;
      console.warn('[stopMeeting] Failed to flush transcript sync:', error);
    } finally {
      clearTimeout(stopRetrying);
    }
  }
  if (recoveryIntent && transcriptManager) {
    recoveryIntent.pendingTranscript = transcriptManager.getPendingTranscript();
    recoveryIntent.syncedLength = transcriptManager.getCurrentOffset();
    if (transcriptSyncFailed) recoveryIntent.reason = 'transcript-sync-failure';
    meetingStore.setInterruptedRecording(recoveryIntent);
  }

  const transcriptionWordCount = meetingStore
    .getTranscriptLines(meetingId)
    .join('\n')
    .split(/\s+/)
    .filter(Boolean).length;
  const wantsDeferredSummary = Boolean(
    options?.deferSummary &&
    !meetingStore.isGuidedOnboardingMeeting(meetingId) &&
    getMeeting(meetingId)?.summary_status === 'initial'
  );
  const deferSummary =
    wantsDeferredSummary &&
    transcriptionWordCount < AUTO_SUMMARIZE_WORD_THRESHOLD;
  // Recovery summarizes speech that failed to sync the way a synced stop would
  if (recoveryIntent && transcriptSyncFailed) {
    recoveryIntent.deferSummary = deferSummary;
    meetingStore.setInterruptedRecording(recoveryIntent);
  }

  if (transcriptManager) {
    if (
      recoveryIntent &&
      !transcriptSyncFailed &&
      shouldFinalizeStoppedMeeting
    ) {
      enteredFinalizePath = true;
      effectiveDeferSummary = deferSummary;
      deferOverriddenByWordThreshold = wantsDeferredSummary && !deferSummary;

      if (deferSummary) coachMarks.show(GENERATE_SUMMARY_COACH_MARK, meetingId);

      const roundLedger = usesLocalPostProcessing
        ? buildRoundLedgerFields({
            meetingId,
            replaceWatermark,
            expectedLength: transcriptManager.getCurrentOffset(),
            deferSummary,
          })
        : undefined;
      recoveryIntent.deferSummary = deferSummary;
      if (roundLedger) {
        recoveryIntent.replaceWatermark = roundLedger.replaceFromOffset;
        recoveryIntent.processingToken = roundLedger.processingToken;
      }
      meetingStore.setInterruptedRecording(recoveryIntent);
      try {
        if (reason.type === 'meeting_presence_lost' && !deferSummary) {
          await showAutomaticallyStoppedMeeting(meetingId);
        }
        if (roundLedger) {
          await startLocalPostProcessing(
            meetingId,
            roundLedger,
            recordingSnapshot.recordingSession
          );
        } else {
          meetingStore.setSummaryFinalizing(meetingId, true);
          await backendApis.meetingNotes.ingestRemainingTranscript(meetingId);
          if (!deferSummary)
            await getStores().meetings.summarizeMeeting(meetingId, {
              automatic: true,
            });
        }
        recoveryIntent.finalizationStarted = true;
        meetingStore.setInterruptedRecording(recoveryIntent);
        if (!usesLocalPostProcessing) {
          if (deferSummary) {
            notifyDeferredSummaryReady(meetingId);
          } else {
            navigateToSummaryTabIfInView(meetingId);
            meetingApis.pollForSummaryToFinish(meetingId, 'auto');
          }
        }
      } catch (error) {
        if (usesLocalPostProcessing)
          recoveryIntent.reason = 'post-processing-start-failure';
        meetingStore.setInterruptedRecording(recoveryIntent);
        logError(
          error,
          usesLocalPostProcessing
            ? '[stopMeeting] Failed to start local post-processing'
            : '[stopMeeting] Failed to finalize transcript'
        );
      } finally {
        if (!usesLocalPostProcessing)
          meetingStore.setSummaryFinalizing(meetingId, false);
      }
    }

    // Clean up the sync manager once a bootstrap completes, unless the
    // meeting was restarted. We wait for bootstrap so the sync engine has an
    // up-to-date transcript before the UI switches away from the local copy.
    // A cycle already in flight may have read the transcript before the
    // final flush, so wait for the first cycle that starts after it.
    if (!transcriptSyncFailed) {
      const completeTranscriptHandoff =
        meetingStore.retainTranscriptUntilBootstrap(meetingId);
      const sdk = getSDK();
      sdk.onNextBootstrap(() => {
        sdk.onNextBootstrap(() => {
          if (completeTranscriptHandoff()) {
            removeManager(meetingId);
          }
        });
      });
    }
  }

  if (!transcriptSyncFailed) {
    try {
      if (recordingSnapshot.recordingSession) {
        await backendApis.meetingNotes.endRecording(
          recordingSnapshot.recordingSession
        );
      }
      if (recoveryIntent?.finalizationStarted)
        meetingStore.clearInterruptedRecording(meetingId);
    } catch (error) {
      logError(error, '[stopMeeting] Failed to release recording session');
    }
  }
  recordingSnapshot.stopRenewingRecordingSession?.();

  // Update the status window
  electronApis.meetings.notifyMeetingStatusUpdate({
    type: 'no_ongoing_meeting',
  });
  electronApis.meetings.dismissMeetingStatusWindow();

  if (transcriptSyncFailed) {
    toast.error(
      'Transcript not saved yet. Your words are kept on this device and will be added when the connection is back.'
    );
  }
  meetingStore.resetRecordingState();

  if (startedAt) {
    const durationMs = recordingStoppedAtMs - startedAt.getTime();
    const lostMeetingAppBundleId =
      reason.type === 'meeting_presence_lost' ? sessionAppBundleId : undefined;
    noteRecordingStopped(meetingId, reason.type, recordingStoppedAtMs);
    trackEvent('MEETING_RECORDING_STOPPED', {
      meeting_id: meetingId,
      duration: Math.floor(durationMs / 1000),
      transcription_words: transcriptionWordCount,
      transcription_word_threshold: AUTO_SUMMARIZE_WORD_THRESHOLD,
      stop_reason: reason.type,
      app_bundle_id: lostMeetingAppBundleId,
      calendar_event_id_hash: hashCalendarEventId(
        getMeeting(meetingId)?.event_id
      ),
      summary_disposition: resolveMeetingRecordingStoppedSummaryDisposition({
        shouldFinalizeStoppedMeeting,
        meetingExistsInBackend:
          transcriptManager?.meetingExistsInBackend === true,
        enteredFinalizePath,
        usesLocalPostProcessing,
        effectiveDeferSummary,
        localPostProcessingStartFailed:
          recoveryIntent?.reason === 'post-processing-start-failure',
        deferOverriddenByWordThreshold,
      }),
    });
  }

  const meetingWasPersistedInBackend =
    transcriptManager?.meetingExistsInBackend === true;
  try {
    options?.onStopped?.({ meetingId, meetingWasPersistedInBackend });
  } catch (error) {
    logError(error, '[stopMeeting] Failed to run onStopped callback');
  }

  subscriptionApis.getStatus();
}

// --- performStartMeetingTransition phases ---
// Each phase below is only called from performStartMeetingTransition; they are
// named steps of that single transition, not independent entry points.

/**
 * Unlike app_detection this never re-runs calendar matching: the prompt already
 * resolved the event with the preceding meeting excluded, and matching again
 * here would land straight back on the meeting the switch is moving away from.
 */
function resolveMeetingSegmentTarget(
  payload: Extract<StartMeetingInput, { startReason: 'meeting_segment' }>
) {
  const existing = payload.eventId
    ? getMeetingByEventId(payload.eventId)
    : undefined;

  return {
    meetingId: existing?.id ?? crypto.randomUUID(),
    isNewMeeting: existing === undefined,
    eventId: payload.eventId,
    appBundleId: payload.appBundleId,
    title: payload.title,
    projectId: undefined,
  };
}

/** Resolves which meeting this start targets: the resumed meeting, the meeting
 * already linked to the matching calendar event, or a brand-new meeting id. */
function resolveStartMeetingTarget(
  payload: StartMeetingInput,
  sessionAppBundleId: string | undefined
) {
  return match(payload)
    .with({ startReason: 'resume' }, (p) => ({
      meetingId: p.meetingId,
      isNewMeeting: false,
      eventId: undefined,
      appBundleId: sessionAppBundleId,
      title: undefined,
      projectId: undefined,
    }))
    .with({ startReason: 'calendar_event' }, (p) => {
      const existing = getMeetingByEventId(p.eventId);
      if (existing) {
        return {
          meetingId: existing.id,
          isNewMeeting: false,
          eventId: p.eventId,
          appBundleId: undefined,
          title: p.title,
          projectId: undefined,
        };
      }
      return {
        meetingId: crypto.randomUUID(),
        isNewMeeting: true,
        eventId: p.eventId,
        appBundleId: undefined,
        title: p.title,
        projectId: undefined,
      };
    })
    .with({ startReason: 'app_detection' }, (p) => {
      const calendarEvent = findActiveCalendarEvent(p.appBundleId, {
        identity: p.meetingKey
          ? { provider: p.provider, meetingKey: p.meetingKey }
          : undefined,
      });
      if (calendarEvent) {
        const existing = getMeetingByEventId(calendarEvent.id);
        if (existing && !recordingStartedOnPreviousLocalDay(existing)) {
          return {
            meetingId: existing.id,
            isNewMeeting: false,
            eventId: calendarEvent.id,
            appBundleId: p.appBundleId,
            title: undefined,
            projectId: undefined,
          };
        }
        if (existing) {
          console.info(
            '[meeting-resolution] starting a new meeting; existing recording is from a previous day',
            { meetingId: existing.id, eventId: calendarEvent.id }
          );
        }
        return {
          meetingId: crypto.randomUUID(),
          isNewMeeting: true,
          eventId: calendarEvent.id,
          appBundleId: p.appBundleId,
          title: calendarEvent.summary,
          projectId: undefined,
        };
      }
      return {
        meetingId: crypto.randomUUID(),
        isNewMeeting: true,
        eventId: undefined,
        appBundleId: p.appBundleId,
        title: undefined,
        projectId: undefined,
      };
    })
    .with({ startReason: 'meeting_segment' }, resolveMeetingSegmentTarget)
    .with({ startReason: 'manual' }, (p) => ({
      meetingId: crypto.randomUUID(),
      isNewMeeting: true,
      eventId: undefined,
      appBundleId: p.appBundleId,
      title: undefined,
      projectId: p.projectId,
    }))
    .exhaustive();
}

type StartMeetingTarget = ReturnType<typeof resolveStartMeetingTarget>;

function prepareInterruptedRecordsForStart(
  resolvedMeetingId: string,
  isNewMeeting: boolean,
  startReason: StartMeetingInput['startReason']
): InterruptedRecordingRecord | undefined {
  let consumedInterruptedRecord: InterruptedRecordingRecord | undefined;
  const resolvedInterruptedRecordId = resolvedMeetingId.toLowerCase();
  for (const interrupted of meetingStore.interruptedRecordings.values()) {
    const isResolvedTarget =
      !isNewMeeting && interrupted.meetingId === resolvedInterruptedRecordId;
    if (isResolvedTarget) {
      consumedInterruptedRecord = interrupted;
      if (interrupted.deferSummary) {
        meetingStore.setSummaryFinalizing(interrupted.meetingId, true);
      }
      ppDebug('start_consumed_interrupted_record', {
        meetingId: interrupted.meetingId,
        startReason,
      });
      continue;
    }
    // Fire-and-forget: enqueued behind this start on the transition queue.
    ppDebug('start_forfeits_interrupted_record', {
      meetingId: interrupted.meetingId,
      startedMeetingId: resolvedInterruptedRecordId,
    });
    void meetingApis.promoteInterruptedMeeting(interrupted.meetingId);
  }
  return consumedInterruptedRecord;
}

/**
 * Seeds this session's replace watermark.
 *
 * Fresh recordings start at 0; ordinary resumes use the subsequent backend fetch.
 *
 * A resume that consumed an interrupted record is different: the crash
 * never acked, so the recording cache still holds every segment since the
 * last finalized round and the next round re-transcribes ALL of them. Its
 * replace boundary is the persisted last-finalized boundary — the fetched
 * backend length would sit past the crashed session's live text and the
 * replace would append a full re-transcription after it (duplication).
 */
function seedResumeWatermark({
  meetingId,
  isNewMeeting,
  resumesInterruptedLocalMeeting,
}: {
  meetingId: string;
  isNewMeeting: boolean;
  resumesInterruptedLocalMeeting: boolean;
}): number | null {
  if (isNewMeeting) {
    // Seed the boundary so a crash before the first finalization resolves to
    // 0 (replace everything) instead of unknown (keep-live)
    meetingStore.setLastFinalizedBoundary(meetingId, 0);
    return 0;
  }
  if (resumesInterruptedLocalMeeting) {
    return meetingStore.getLastFinalizedBoundary(meetingId);
  }
  return null;
}

/**
 * Wires live transcript delta routing for the session: utterance deltas flow
 * through the ordering sink, legacy deltas through the normalizer, and finals
 * are handed to the sync manager. Returns the updated loading state and the
 * teardown that also flushes the sink.
 */
function wireTranscriptDeltaRouting({
  meetingId,
  transcriptManager,
  loadingState,
}: {
  meetingId: string;
  transcriptManager: TranscriptSyncManager;
  loadingState: Extract<RecordingState, { status: 'loading' }>;
}): {
  loadingState: Extract<RecordingState, { status: 'loading' }>;
  unlistenTranscriptDelta: () => void;
} {
  const remoteTranscriptDeltaNormalizer =
    createRemoteTranscriptDeltaNormalizer();
  // The sink owns gate-store-sync ordering: finals reach the backend in
  // open order, so a long-lived utterance on one lane cannot land below
  // later speech
  const utteranceDeltaSink = createUtteranceDeltaSink({
    applyDelta: (delta) =>
      meetingStore.applyUtteranceTranscriptDelta(meetingId, delta),
    syncFinal: (released) => {
      // Local mode streams to the backend exactly like live mode; the
      // post-processed transcript replaces this live tail at finalization.
      transcriptManager.addDelta(
        { speaker: released.speaker, text: released.text },
        { chunkId: released.chunkId }
      );
    },
  });

  // Store the teardown in recording state before registering the listener:
  // if registration throws, the start-error stop transition can still flush
  // the sink and clear its overdue-sweep interval
  let rawUnlistenTranscriptDelta: (() => void) | null = null;
  const unlistenTranscriptDelta = () => {
    rawUnlistenTranscriptDelta?.();
    // The stop drain has delivered every final it could by now; release
    // anything still held (an utterance whose final never arrived) so its
    // ordering hold cannot turn into data loss
    utteranceDeltaSink.flush();
  };
  const updatedLoadingState = { ...loadingState, unlistenTranscriptDelta };
  meetingStore.setRecording(updatedLoadingState);
  rawUnlistenTranscriptDelta = electronApis.meetings.listenTranscriptDelta(
    (delta) => {
      if (isUtteranceDelta(delta)) {
        utteranceDeltaSink.receive(delta);
        return;
      }

      // The recording state is the single source for the engine; the
      // stopping variant carries it so stop-time deltas still route right
      const recording = meetingStore.recording;
      const activeTranscriptionEngine =
        recording.status === 'loading' ||
        recording.status === 'recording' ||
        recording.status === 'stopping'
          ? recording.activeTranscriptionEngine
          : undefined;
      const isLocalTranscription = activeTranscriptionEngine === 'local';
      const normalizedDelta = isLocalTranscription
        ? delta
        : remoteTranscriptDeltaNormalizer.normalize(delta);

      meetingStore.appendTranscriptDelta(meetingId, normalizedDelta);
      if (normalizedDelta.isFinal) {
        transcriptManager.addDelta(
          {
            speaker: normalizedDelta.speaker,
            text: normalizedDelta.text,
          },
          { chunkId: normalizedDelta.chunkId }
        );
      }
    }
  );

  return { loadingState: updatedLoadingState, unlistenTranscriptDelta };
}

async function fetchStartRecordingDeviceMetadata(): Promise<{
  recordingDeviceId: string;
  recordingPlatform: string | undefined;
}> {
  const recordingDeviceId = await electronApis.system.getDeviceId();
  if (!recordingDeviceId) throw new Error('Recording device ID is unavailable');
  let recordingPlatform: string | undefined;
  try {
    recordingPlatform = await electronApis.system.getPlatform();
  } catch (error) {
    logError(
      error,
      '[startMeeting] getPlatform failed (best-effort, continuing)'
    );
  }
  return { recordingDeviceId, recordingPlatform };
}

function createBackendMeetingForStart({
  target,
  recordingDeviceId,
  recordingPlatform,
}: {
  target: StartMeetingTarget;
  recordingDeviceId: string;
  recordingPlatform: string | undefined;
}): Promise<void> {
  const { meetingId, eventId, title, appBundleId, projectId } = target;
  const appBoundWhileStartWasLoading = meetingStore.recordingSessionAppBundleId;
  return new Promise((resolve, reject) => {
    getStores().meetings.createMeeting({
      id: meetingId,
      eventId,
      name: title,
      sourceApp: appBundleId ?? appBoundWhileStartWasLoading,
      projectId,
      recordingDeviceId,
      recordingPlatform,
      onPersist: resolve,
      onError: reject,
    });
  });
}

/** Flips the store to `recording`, announces the started meeting to native and
 * the status window, and navigates to the meeting's notes tab. */
function activateRecordingSession({
  meetingId,
  eventId,
  recordingSession,
  payload,
  loadingState,
  transcriptManager,
  transcriptLengthAtStart,
  replaceWatermark,
  unlistenTranscriptDelta,
  activeTranscriptionEngine,
}: {
  meetingId: string;
  eventId: string | undefined;
  recordingSession: RecordingSession;
  payload: StartMeetingInput;
  loadingState: Extract<RecordingState, { status: 'loading' }>;
  transcriptManager: TranscriptSyncManager;
  transcriptLengthAtStart: number;
  replaceWatermark: number | null;
  unlistenTranscriptDelta: () => void;
  activeTranscriptionEngine: TranscriptionEngine | undefined;
}) {
  const unlistenPostStartNativeEvents =
    meetingApis.listenToPostStartNativeEvents();

  electronApis.meetings.notifyMeetingStarted(meetingId, eventId);
  clearUnexpectedStop(meetingId);
  meetingStore.setRecording({
    status: 'recording',
    meetingId,
    recordingSession,
    startedAt: new Date(),
    activeTranscriptionEngine,
    usesLocalPostProcessing: loadingState.usesLocalPostProcessing,
    transcriptManager,
    transcriptLengthAtStart,
    replaceWatermark,
    unlistenTranscriptDelta,
    unlistenPostStartNativeEvents,
    stopRenewingRecordingSession: loadingState.stopRenewingRecordingSession,
  });
  meetingStore.lastAudioReceivedAt = new Date();

  updateStoreMeeting(meetingId, {
    id: meetingId,
  });

  trackMeetingStart(meetingId, payload, eventId);

  const fullMeeting = storeMeetingToMeeting(
    getStores().meetings.get(meetingId)
  );
  if (fullMeeting) {
    electronApis.meetings.notifyMeetingStatusUpdate({
      type: 'ongoing',
      meeting: fullMeeting,
      activeTranscriptionEngine,
    });
  } else {
    console.error(
      "Meeting doesn't exist in store, despite adding it to the store above"
    );
  }

  navigate({
    to: '/meetings/$meetingId',
    params: { meetingId: meetingId.toString() },
    search: { tab: 'notes' },
  });
}

/**
 * RUNTIME INVARIANTS: The correct functioning of this function depends on the runtime invariant that:
 *
 *   1. No two of the `perform*Transition()` functions (`performStartMeetingTransition()`,
 *      `performStopMeetingTransition()`, and `performPromoteInterruptedTransition()` /
 *      `performRecoverInterruptedTransition()` from meetings.recovery.ts) run concurrently. EXCEPT:
 *       i) the call to `performStopMeetingTransition()` in the `catch` statement of the big try/catch below, and
 *      ii) the call to `performStopMeetingTransition()` to stop an existing meeting right at the beginning
 *          of `performStartMeetingTransition()` to ensure that there's no active meeting.
 *
 *   2. `meetingStore.recording` is modified only inside those `perform*Transition()` functions.
 *
 *  We enforce this invariant currently by:
 *   1. exposing only the `enqueue*`/`promoteInterruptedMeeting`/`recoverInterruptedLocalPostProcessingRecordings`
 *      entry points outside this file,
 *   2. those functions using `TransitionQueue` to avoid concurrency,
 *   3. and meetings.recovery.ts never calling its `perform*Transition()` functions itself: its
 *      promotion timers go through the enqueue injected via `setPromoteInterruptedEnqueue`.
 **/
async function performStartMeetingTransition({
  payload,
  onMeetingStarted,
}: {
  payload: StartMeetingInput;
  onMeetingStarted?: (meetingId: string) => void;
}) {
  let transitionMeetingId: string | undefined;
  let transitionAppBundleId: string | undefined;
  let creatingBackendMeeting = false;
  const clearStartFailureState = () => {
    if (meetingStore.isGuidedOnboardingRecording) {
      meetingStore.setIsGuidedOnboardingRecording(false);
    }
    meetingStore.meetingStartedFromOverview = false;
  };
  const abandonCancelledAutoStart = async () => {
    if (
      !isAutoStart(payload) ||
      meetingAppDetections.isAutoStartLive(payload.autoStartAttemptId)
    ) {
      return false;
    }
    await performStopMeetingTransition({ type: 'start_error' });
    const nativeStopFailed = meetingStore.recording.status !== 'idle';
    if (nativeStopFailed) return false;
    clearStartFailureState();
    logStartTransitionResult({
      payload,
      outcome: 'failure',
      meetingId: transitionMeetingId,
      appBundleId: transitionAppBundleId,
      failureReason: 'auto_start_cancelled',
    });
    return true;
  };

  try {
    if (!isCapable(AppCapability.RecordMeeting))
      throw new Error('No meeting recording capability');

    const autoStartRejection = getAutoStartRejection(payload);
    if (autoStartRejection) {
      logStartTransitionResult({
        payload,
        outcome: 'failure',
        failureReason: autoStartRejection,
      });
      return;
    }

    if (!meetingStore.hasMicrophonePermission) {
      // This re-prompts when status is not determined
      // If mic was already denied, this opens System Settings > Privacy & Security > Microphone
      const updatedPermissions = await meetingApis.requestMacPermissions([
        'microphone',
      ]);

      if (!updatedPermissions.mic) {
        // Do not show a relaunch fallback here
        // If mic access was denied, users must enable it in System Settings
        toast.error(
          'Unable to start meeting. Please enable microphone access in System Settings and try again.'
        );
        throw new Error(
          'Unable to start meeting. Microphone permission denied'
        );
      }
      if (await abandonCancelledAutoStart()) return;
    }

    if (subscriptionStore.hasReachedMeetingLimit) {
      uiStore.showPaywallBanner({
        title: 'Meeting limit reached',
        subtitle: 'Upgrade to unlock unlimited meeting recording.',
      });
      logStartTransitionResult({
        payload,
        outcome: 'failure',
        failureReason: 'meeting_limit_reached',
      });
      return;
    }

    const recording = meetingStore.recording;
    const { precedingMeetingId, precedingRecordingStopReason } =
      getPrecedingRecordingSwitch(payload);
    if (
      precedingMeetingId &&
      (recording.status === 'idle' ||
        recording.meetingId !== precedingMeetingId)
    ) {
      logStartTransitionResult({
        payload,
        outcome: 'failure',
        failureReason: 'stale_switch_request',
      });
      return;
    }

    if (recording.status !== 'idle') {
      // If we do `await enqueueStopMeeting()` here, it's possible that there's
      // a start meeting transition that was enqueued before it, so it gets weird if we do that.
      await performStopMeetingTransition({
        type: precedingRecordingStopReason ?? 'stop_note_taking',
      });
      if (meetingStore.recording.status !== 'idle') return;
    }

    const sessionAppBundleId = getInitialSessionAppBundleId(payload);
    const startTarget = resolveStartMeetingTarget(payload, sessionAppBundleId);
    const startEvent =
      startTarget.eventId && findCalendarEventById(startTarget.eventId);
    if (startEvent && (await isBlockedByTeamRecordingRules(startEvent))) {
      toast.error("Your team doesn't record meetings like this one.");
      logStartTransitionResult({
        payload,
        outcome: 'failure',
        failureReason: 'team_recording_rule',
      });
      return;
    }
    if (await abandonCancelledAutoStart()) return;
    if (getMeeting(startTarget.meetingId)?.transcript_deleted_at) {
      toast.error("Your team's data policy removed this meeting's transcript.");
      logStartTransitionResult({
        payload,
        outcome: 'failure',
        failureReason: 'transcript_removed_by_policy',
      });
      return;
    }
    // Notes synced from another app have no recording to resume, and the backend refuses one
    if (getMeeting(startTarget.meetingId)?.external_source) {
      toast.error("Notes from another app can't be recorded here.");
      logStartTransitionResult({
        payload,
        outcome: 'failure',
        failureReason: 'synced_note',
      });
      return;
    }

    const { meetingId, isNewMeeting, appBundleId } = startTarget;
    transitionMeetingId = meetingId;
    transitionAppBundleId = appBundleId;

    const consumedInterruptedRecord = prepareInterruptedRecordsForStart(
      meetingId,
      isNewMeeting,
      payload.startReason
    );

    // Resume-while-parked: replace the parked round's suffix now, while it is
    // still a valid suffix (before this session appends). On failure the round
    // degrades to keep-live so the fresh fetch below seeds a clean watermark.
    if (!isNewMeeting) {
      await resolveParkedRoundBeforeResume(meetingId);
      if (await abandonCancelledAutoStart()) return;
    }

    if (meetingStore.guidedOnboardingStartPending) {
      meetingStore.setIsGuidedOnboardingRecording(true, meetingId);
    }

    const resumesInterruptedLocalMeeting =
      consumedInterruptedRecord?.usesLocalPostProcessing === true;
    let replaceWatermark = seedResumeWatermark({
      meetingId,
      isNewMeeting,
      resumesInterruptedLocalMeeting,
    });
    let loadingState: Extract<RecordingState, { status: 'loading' }> = {
      status: 'loading',
      meetingId,
      electronApiStartMeetingWasHit: false,
      transcriptManager: null,
      unlistenTranscriptDelta: null,
      usesLocalPostProcessing: false,
      replaceWatermark,
    };
    meetingStore.setRecordingSessionAppBundleId(sessionAppBundleId);
    meetingStore.setRecordingSessionStartReason(payload.startReason);
    meetingStore.setRecordingSessionExpectsCall(
      startExpectsCall(payload, startEvent || undefined)
    );
    meetingStore.setRecording(loadingState);
    const { recordingDeviceId, recordingPlatform } =
      await fetchStartRecordingDeviceMetadata();
    if (await abandonCancelledAutoStart()) return;
    if (isNewMeeting) {
      creatingBackendMeeting = true;
      const backendMeetingCreated = createBackendMeetingForStart({
        target: startTarget,
        recordingDeviceId,
        recordingPlatform,
      });
      if (payload.startReason === 'manual') {
        navigate({
          to: '/meetings/$meetingId',
          params: { meetingId },
          search: { tab: 'notes' },
        });
      }
      await backendMeetingCreated;
      creatingBackendMeeting = false;
      if (await abandonCancelledAutoStart()) return;
    }
    const recordingSession: RecordingSession = {
      meetingId,
      deviceId: recordingDeviceId,
      sessionId: crypto.randomUUID(),
    };
    loadingState = { ...loadingState, recordingSession };
    meetingStore.setRecording(loadingState);
    let refreshedTranscript = await backendApis.meetingNotes.resumeRecording(
      recordingSession,
      payload.startReason === 'resume' ? payload.takeoverOtherDevice : undefined
    );
    recordingSession.transcriptRevision =
      refreshedTranscript.transcriptRevision;
    loadingState = {
      ...loadingState,
      stopRenewingRecordingSession: keepRecordingSessionAlive(
        recordingSession,
        () => {
          const current = meetingStore.recording;
          if (
            (current.status === 'recording' || current.status === 'loading') &&
            current.recordingSession?.sessionId === recordingSession.sessionId
          ) {
            void meetingApis.enqueueStopMeeting({ type: 'sync_failure' });
            alertUnexpectedRecordingStop({
              meetingId: recordingSession.meetingId,
              ...SESSION_LOST_STOP_ALERT,
            });
          }
        }
      ),
    };
    meetingStore.setRecording(loadingState);
    if (await abandonCancelledAutoStart()) return;
    if (consumedInterruptedRecord?.pendingTranscript) {
      const replay = await retryInterruptedTranscript(
        consumedInterruptedRecord,
        recordingSession,
        refreshedTranscript
      );
      refreshedTranscript = {
        ...refreshedTranscript,
        ...(await getBackendTranscriptForResume(meetingId)),
      };
      if (await abandonCancelledAutoStart()) return;
      // The round's audio starts before the recovered speech, so replacing
      // from any offset would add a second copy of it; keep the live text
      if (replay?.movedToEnd) replaceWatermark = null;
    } else if (
      consumedInterruptedRecord &&
      hasTextAfterSyncedLength(
        consumedInterruptedRecord,
        refreshedTranscript.transcript
      )
    ) {
      replaceWatermark = null;
    }
    meetingStore.initializeTranscript(meetingId);
    const transcriptLengthAtStart = refreshedTranscript.currentOffset;
    runInAction(() => {
      meetingStore.transcripts.set(meetingId, refreshedTranscript.transcript);
    });
    if (!resumesInterruptedLocalMeeting) {
      replaceWatermark = refreshedTranscript.currentOffset;
    }
    loadingState = { ...loadingState, replaceWatermark };

    // A resume before bootstrap must not reuse the previous round's offset
    removeManager(meetingId);
    setLiveSpeechUnsynced(meetingId, false);
    const transcriptManager = getOrCreateManager({
      meetingId,
      patchTranscript: (_mid, startOffset, delta, speaker, signal) =>
        backendApis.meetingNotes.patchTranscript(
          recordingSession,
          startOffset,
          delta,
          speaker,
          signal
        ),
      isRetryableError: isTransientTranscriptSyncError,
      onUnsyncedChange: (unsynced) =>
        setLiveSpeechUnsynced(meetingId, unsynced),
      onFailure: (error) => {
        const current = meetingStore.recording;
        if (
          (current.status !== 'loading' && current.status !== 'recording') ||
          current.recordingSession?.sessionId !== recordingSession.sessionId
        )
          return;
        console.error(
          '[startMeeting] Transcript sync failed, stopping meeting'
        );
        meetingApis.enqueueStopMeeting({ type: 'sync_failure' });
        // A 409 means this device no longer holds the recording session, the same as a failed lease renewal
        if (error instanceof HttpError && error.status === 409) {
          alertUnexpectedRecordingStop({
            meetingId: recordingSession.meetingId,
            ...SESSION_LOST_STOP_ALERT,
          });
          return;
        }
        alertUnexpectedRecordingStop({
          meetingId: recordingSession.meetingId,
          cause: 'transcript_rejected',
          title: 'Recording stopped',
          message:
            "Recording stopped because Littlebird couldn't save the transcript to the server. What was captured is kept on this device. Resume to keep recording.",
        });
      },
      currentOffset: transcriptLengthAtStart,
      meetingExistsInBackend: true,
      // Set before meeting start: localStorage.setItem('localTranscriptionDebug', 'true')
      trace: getLocalTranscriptionSyncTraceSink(),
    });
    loadingState = { ...loadingState, transcriptManager };
    meetingStore.setRecording(loadingState);

    const transcriptDeltaRouting = wireTranscriptDeltaRouting({
      meetingId,
      transcriptManager,
      loadingState,
    });
    loadingState = transcriptDeltaRouting.loadingState;
    const { unlistenTranscriptDelta } = transcriptDeltaRouting;

    const transcriptionInputSelection =
      settingsStore.preferredMeetingTranscriptionInputDevice;
    const preferredTranscriptionEngine = resolvePreferredTranscriptionEngine();

    loadingState = { ...loadingState, nativeStartRequested: true };
    meetingStore.setRecording(loadingState);
    const activeTranscriptionEngine = await electronApis.meetings.startMeeting({
      meetingId,
      appBundleId,
      startTrigger: startTriggerFor(payload),
      transcriptionInputSelection,
      preferredTranscriptionEngine,
      customVocabulary: resolveStartCustomVocabulary(startTarget),
      detectionId:
        payload.startReason === 'app_detection'
          ? payload.detectionId
          : undefined,
      autoStartAttemptId:
        payload.startReason === 'app_detection'
          ? payload.autoStartAttemptId
          : undefined,
      notificationId:
        payload.startReason === 'app_detection'
          ? payload.notificationId
          : undefined,
    });
    loadingState = {
      ...loadingState,
      electronApiStartMeetingWasHit: true,
      activeTranscriptionEngine,
      // Latch the post-processing decision now that the engine is known; a
      // mid-meeting feature-flag flip must not re-route this meeting.
      usesLocalPostProcessing: shouldUseLocalPostProcessing(
        activeTranscriptionEngine
      ),
    };
    meetingStore.setRecording(loadingState);
    if (await abandonCancelledAutoStart()) return;

    if (!isNewMeeting) {
      void getStores()
        .meetings.updateMeeting(meetingId, {
          recordingDeviceId,
          recordingPlatform,
        })
        .catch((err) => {
          logError(
            err,
            '[startMeeting] updateMeeting(recording metadata) failed after start (best-effort)'
          );
        });
    }

    activateRecordingSession({
      meetingId,
      eventId:
        startTarget.eventId ?? getMeeting(meetingId)?.event_id ?? undefined,
      recordingSession,
      payload,
      loadingState,
      transcriptManager,
      transcriptLengthAtStart,
      replaceWatermark,
      unlistenTranscriptDelta,
      activeTranscriptionEngine,
    });

    meetingStore.clearInterruptedRecording(meetingId);
    withdrawMeetingAppOffer(
      meetingAppDetections.cancelOtherAutoStarts(
        payload.startReason === 'app_detection'
          ? payload.autoStartAttemptId
          : undefined
      )
    );
    onMeetingStarted?.(meetingId);
    subscriptionApis.getStatus();
    logStartTransitionResult({
      payload,
      outcome: 'success',
      meetingId,
      appBundleId,
      transcriptionEngine: activeTranscriptionEngine,
    });
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    const ownedByOtherDevice =
      err instanceof HttpError &&
      extractBackendErrorCode(err.body) ===
        'meeting_recording_owned_by_other_device';
    if (
      (ownedByOtherDevice || error instanceof MeetingQuotaExceededError) &&
      meetingStore.recording.status === 'loading'
    ) {
      meetingStore.setRecording({
        ...meetingStore.recording,
        recordingSession: undefined,
      });
    }
    const contextKitDeclinedThisStart =
      error instanceof NativeStartRejectedError;
    if (
      contextKitDeclinedThisStart &&
      meetingStore.recording.status === 'loading'
    ) {
      meetingStore.setRecording({
        ...meetingStore.recording,
        nativeStartRequested: false,
      });
    }

    logStartTransitionResult({
      payload,
      outcome: 'failure',
      meetingId: transitionMeetingId,
      appBundleId: transitionAppBundleId,
      error,
    });
    logIfContextKitRefusedStartWhileAppIdle({
      payload,
      meetingId: transitionMeetingId,
      error,
    });

    if (transitionMeetingId)
      meetingStore.setSummaryFinalizing(transitionMeetingId, false);

    // Run cleanup now inside the current transition, because if we do `await enqueueStopMeeting()`
    // here, it's possible that there's a start meeting transition that was enqueued before it.
    await performStopMeetingTransition({
      type: creatingBackendMeeting ? 'backend_create_failed' : 'start_error',
    });
    clearStartFailureState();

    logError(error, 'Failed to start meeting');

    if (error instanceof MeetingCreationCancelledError) return;

    if (ownedByOtherDevice && transitionMeetingId) {
      const meetingId = transitionMeetingId;
      toast.error('This meeting is recording on another device.', {
        description: 'If that recording has stopped, you can continue here.',
        duration: 15000,
        action: {
          label: 'Record here',
          onClick: () =>
            meetingApis.enqueueStartMeeting({
              payload: {
                startReason: 'resume',
                meetingId,
                takeoverOtherDevice: true,
                resumeSource: 'record_here',
              },
              onMeetingStarted,
            }),
        },
      });
      return;
    }

    if (creatingBackendMeeting) {
      if (error instanceof MeetingQuotaExceededError) {
        uiStore.showPaywallBanner({
          title: 'Meeting limit reached',
          subtitle: error.errorMessage,
        });
      } else {
        toast.error(
          'Unable to start meeting. Failed to save meeting to server.'
        );
      }
      if (transitionMeetingId && getStores().meetings.get(transitionMeetingId))
        return;
      navigate({ to: '/meetings' });
      return;
    }

    dispatchStartFailedNotification(payload);

    if (isDebug) errorToast(error);
  }
}

export function dispatchStartFailedNotification(
  payload: StartMeetingInput
): void {
  electronApis.lbNotifications
    .sendNotification({
      id: crypto.randomUUID(),
      view: 'meeting-start-failed',
      sound: 'problem',
      expiresAt: LbNotificationExpiry.alert(),
      payload: {
        meetingLabel: getMeetingLabelForFailureNotification(payload),
        provider: getProviderForFailureNotification(payload),
        retryPayload: buildRetryPayload(payload),
      },
    })
    .catch((error) => {
      logError(error, 'Failed to dispatch meeting-start-failed notification');
    });
}

// Derived once from the schema so adding a field to AppDetectionAttemptFieldsSchema
// automatically strips it on retry. Adding to AppDetectionSemanticFieldsSchema
// flows through untouched.
const APP_DETECTION_ATTEMPT_FIELD_KEYS = Object.keys(
  AppDetectionAttemptFieldsSchema.shape
) as (keyof z.infer<typeof AppDetectionAttemptFieldsSchema>)[];

export function buildRetryPayload(
  payload: StartMeetingInput
): StartMeetingInput {
  if (
    payload.startReason === 'resume' &&
    payload.resumeSource === 'contextkit_recovery'
  ) {
    return { ...payload, resumeSource: 'notification' };
  }
  if (payload.startReason !== 'app_detection') return payload;
  const cleaned: Record<string, unknown> = { ...payload };
  for (const key of APP_DETECTION_ATTEMPT_FIELD_KEYS) delete cleaned[key];
  cleaned.autoStartAttemptId = crypto.randomUUID();
  // Cast: TS can't narrow after delete; we restored app_detection's required keys above.
  return cleaned as Extract<
    StartMeetingInput,
    { startReason: 'app_detection' }
  >;
}

function getProviderForFailureNotification(
  payload: StartMeetingInput
): MeetingProvider | undefined {
  return match(payload)
    .with(
      { startReason: 'app_detection' },
      (p) => p.provider ?? getProviderFromBundleId(p.appBundleId)
    )
    .with({ startReason: 'calendar_event' }, (p) => p.provider)
    .with(
      { startReason: 'meeting_segment' },
      (p) => p.provider ?? getProviderFromBundleId(p.appBundleId)
    )
    .with({ startReason: 'manual' }, (p) =>
      getProviderFromBundleId(p.appBundleId)
    )
    .with({ startReason: 'resume' }, () => undefined)
    .exhaustive();
}

export function getMeetingLabelForFailureNotification(
  payload: StartMeetingInput
): string {
  return match(payload)
    .with(
      { startReason: 'app_detection' },
      (p) => p.appName ?? p.appBundleId ?? 'Meeting'
    )
    .with({ startReason: 'calendar_event' }, (p) => p.title)
    .with(
      { startReason: 'meeting_segment' },
      (p) => p.title ?? p.appName ?? p.appBundleId ?? 'Meeting'
    )
    .with(
      { startReason: 'resume' },
      (p) => getMeeting(p.meetingId)?.name ?? 'Meeting'
    )
    .with({ startReason: 'manual' }, (p) => p.appBundleId ?? 'Meeting')
    .exhaustive();
}

/** Shared by getCalendarEventsQueryOptions and findActiveCalendarEvent so the query key the
 * latter reads from the cache can't drift from the key the former actually fetches under. */
function getCalendarEventsQueryKey(
  timeZone: string,
  excludeSoloEvents: boolean
) {
  return [
    'calendar',
    'events',
    timeZone,
    config.calendar.days_ahead,
    config.calendar.max_events,
    excludeSoloEvents,
    config.calendar.excluded_event_types as EventType[],
    config.calendar.excluded_statuses,
  ] as const;
}

function getCalendarEventsQueryOptions(excludeSoloEvents: boolean) {
  const timeZone = getDeviceTimeZone();
  return {
    queryKey: getCalendarEventsQueryKey(timeZone, excludeSoloEvents),
    queryFn: async (): Promise<CalendarEvent[]> => {
      return await backendApis.calendar.getEvents({
        daysAhead: config.calendar.days_ahead,
        maxEvents: config.calendar.max_events,
        excludeSoloEvents,
        excludedEventTypes: config.calendar.excluded_event_types as EventType[],
        excludedStatuses: config.calendar.excluded_statuses,
      });
    },
    refetchInterval: CALENDAR_REFETCH_INTERVAL,
    staleTime: CALENDAR_REFETCH_INTERVAL / 2,
    retry: 2,
  };
}

const shareDraftKey = (meetingId: MeetingId, accountLabel: string) =>
  ['meeting-share-draft', meetingId, accountLabel] as const;

const summaryErrorToast = (
  title: string,
  onRetry: () => void,
  toastId?: string
) => {
  toast.error(title, {
    id: toastId,
    description: 'Check your connection and try again.',
    closeButton: true,
    action: { label: 'Retry', onClick: onRetry },
  });
};

/**
 * A collection of all the back-end API calls for meetings
 * */
const MEETING_CIRCLES_STALE_MS = 60_000;

const calendarCirclesVersion = (calendarEvents: CalendarEvent[]) =>
  hashString(
    JSON.stringify(
      calendarEvents.map((event) => [
        event.id,
        event.calendar_series_id,
        event.start_datetime,
        event.summary,
        event.attendees?.map((attendee) => [
          attendee.email,
          attendee.self,
          attendee.responseStatus,
        ]),
      ])
    )
  );

const circleDisplayName = (email: string, name: string | null) =>
  getAttendeeDisplayName({ email, displayName: name }) ?? email;

export const meetingApis = {
  /** Hook to build the meetings sidebar circles from every stored meeting */
  useMeetingCirclesQuery({
    accountId,
    currentUserEmail,
    meetingsVersion,
  }: {
    accountId: string | null;
    currentUserEmail: string | null;
    meetingsVersion: string;
  }) {
    const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery();
    const calendarVersion = useMemo(
      () => calendarCirclesVersion(calendarEvents),
      [calendarEvents]
    );
    const version = `${meetingsVersion}:${calendarVersion}`;

    useEffect(() => {
      if (accountId) refreshMeetingCirclesIfChanged(accountId, version);
    }, [accountId, version]);

    return useQuery({
      queryKey: ['meetings', 'circles', accountId],
      queryFn: async () => {
        const circles = buildMeetingCircles({
          meetings: await getStores().meetings.loadAttendeeSummaries(),
          calendarEventsById: new Map(
            calendarEvents.map((event) => [event.id, event])
          ),
          currentUserEmail,
          displayName: circleDisplayName,
        });
        if (accountId) writeCachedMeetingCircles(accountId, circles);
        return circles;
      },
      placeholderData: () =>
        accountId ? readCachedMeetingCircles(accountId) : undefined,
      enabled: accountId !== null,
      staleTime: MEETING_CIRCLES_STALE_MS,
    });
  },

  /** Hook to load the given meetings into the store */
  useMeetingsByIdsQuery(key: string | null, meetingIds: string[]) {
    return useQuery({
      queryKey: ['meetings', 'by-ids', key, meetingIds],
      queryFn: async () => {
        await getStores().meetings.loadByIds(meetingIds);
        return true;
      },
      placeholderData: (previous: boolean | undefined, previousQuery) =>
        previousQuery?.queryKey[2] === key ? previous : undefined,
      enabled: key !== null,
    });
  },

  useTranscriptDeletionEnabledQuery() {
    return useQuery({
      queryKey: ['meetings', 'transcript-deletion'],
      queryFn: () => backendApis.meetingNotes.fetchTranscriptDeletionEnabled(),
    });
  },

  /** Hook to fetch calendar events */
  useCalendarEventsQuery(options?: {
    enabled?: boolean;
    excludeSoloEvents?: boolean;
    refetchIntervalInBackground?: boolean;
  }) {
    const excludeSoloEvents =
      options?.excludeSoloEvents ?? config.meetings.exclude_solo_events;
    return useQuery({
      ...getCalendarEventsQueryOptions(excludeSoloEvents),
      enabled: options?.enabled ?? true,
      refetchIntervalInBackground: options?.refetchIntervalInBackground,
    });
  },

  /**
   * Create a Gmail draft of the meeting summary for the other attendees.
   * Cached per meeting so reopening the dialog (or coming back to the meeting)
   * reuses the draft instead of creating another one in the user's Gmail;
   * `removeShareDraft` forgets it once it has been sent.
   */
  useShareDraftQuery(
    meetingId: MeetingId,
    accountLabel: string,
    enabled: boolean
  ) {
    return useQuery({
      queryKey: shareDraftKey(meetingId, accountLabel),
      queryFn: () =>
        backendApis.meetingNotes.createShareDraft(meetingId, accountLabel),
      enabled,
      staleTime: Infinity,
      retry: false,
    });
  },

  /** Forget the cached share draft so the next open creates a fresh one */
  removeShareDraft(meetingId: MeetingId, accountLabel: string) {
    queryClient.removeQueries({
      queryKey: shareDraftKey(meetingId, accountLabel),
    });
  },

  /** Prefetch calendar events into the cache; safe to call multiple times. */
  prefetchCalendarEvents() {
    return queryClient.prefetchQuery(
      getCalendarEventsQueryOptions(config.meetings.exclude_solo_events)
    );
  },

  /**
   * Get a single meeting by ID from the sync store.
   * Note: For reactive access, use `useStores().meetings.get(id)` with `observer()`.
   *
   * This hook still fetches from API if the meeting is not in the store,
   * which can happen during navigation before sync completes.
   */
  useFetchById(meetingId?: MeetingId, automaticFetch = false) {
    const [loading, setLoading] = useState(!getMeeting(meetingId));
    const [error, setError] = useState<Error | null>(null);

    const fetchMeeting = useCallback(async () => {
      if (!meetingId) return null;

      const existing = getMeeting(meetingId);
      if (existing) {
        setLoading(false);
        return existing;
      }

      try {
        await meetingApis.fetchMeetingById(meetingId);
      } catch (error) {
        console.error(error);
        errorToast(error as Error);
        setError(error as Error);
      }
      setLoading(false);
    }, [meetingId]);

    useEffect(() => {
      if (automaticFetch) fetchMeeting();
    }, [automaticFetch, fetchMeeting]);

    return { fetchMeeting, loading, error };
  },

  /** Fetches a meeting by id from the backend API */
  async fetchMeetingById(id: MeetingId): Promise<Meeting | undefined> {
    if (!id) return undefined;
    try {
      const meeting = await backendApis.meetingNotes.getById(id);
      if (!meeting) return undefined;
      updateStoreMeeting(id, meeting);
      return meeting;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  },

  /** Hook to update a meeting */
  updateMeeting(update: Partial<Meeting> & { id?: MeetingId }) {
    debouncedUpdateMeeting(update);
    // Flush, not cancel: cancel drops the queued edit and strands its
    // tracker entry
    return () => {
      if (update.id) meetingUpdateDebouncers.get(update.id)?.flush();
    };
  },

  updateMeetingSummary(
    update: Parameters<typeof debouncedUpdateMeetingSummary>[0]
  ) {
    debouncedUpdateMeetingSummary(update);
    return () => meetingSummaryDebouncers.get(update.id)?.flush();
  },

  async reindexMeetingSearch(meetingId: MeetingId) {
    if (!hasSDK()) return;

    const sdk = getSDK();
    const meeting = getStores().meetings.get(meetingId);

    // Always remove stale docs on route exit, even if the meeting no longer exists in store
    await sdk.searchStore.reindexItems({
      removeIds: getMeetingSearchDocIds(meetingId),
      addItems: meeting ? buildMeetingSearchItems(meeting) : [],
    });
  },

  /**
   * Sync meeting from backend and watch for summary completion.
   * Uses MobX reaction to watch for summary status changes via the sync engine.
   */
  pollForSummaryToFinish(
    meetingId: MeetingId,
    startMethod: SummaryStartMethod,
    options?: { onFinish?: () => void }
  ) {
    const sdk = getSDK();
    const { meetings } = getStores();

    trackEvent('MEETING_SUMMARY_STARTED', {
      meeting_id: meetingId,
      start_method: startMethod,
    });

    // Speed up polling during summarization (matches previous 500ms behavior)
    sdk.setPollingInterval(POLLING_INTERVAL_MS);

    let disposer: (() => void) | undefined;
    let shouldDisposeAfterInit = false;
    const disposeSummaryPolling = () => {
      sdk.resetPollingInterval();
      if (disposer) {
        disposer();
      } else {
        shouldDisposeAfterInit = true;
      }
    };

    disposer = reaction(
      () => meetings.get(meetingId)?.summaryStatus,
      (summaryStatus) => {
        if (
          ['completed', 'no_transcript', 'failed', 'skipped'].includes(
            summaryStatus ?? ''
          )
        ) {
          disposeSummaryPolling();

          const meeting = getMeeting(meetingId);
          trackEvent('MEETING_SUMMARY_COMPLETED', {
            meeting_id: meetingId,
            summary_words: meeting?.summary?.split(' ').length ?? 0,
          });

          if (meetingStore.isGuidedOnboardingMeeting(meetingId)) {
            const completedMeeting = getMeeting(meetingId);
            if (summaryStatus === 'completed' && completedMeeting?.summary) {
              meetingStore.setOnboardingChecklistCompleted(true);
              // Clear guided state after a delay so the coaching banner can show "complete"
              setTimeout(() => {
                if (meetingStore.isGuidedOnboardingMeeting(meetingId)) {
                  meetingStore.setIsGuidedOnboardingRecording(false);
                }
              }, 5000);
            } else {
              meetingStore.setIsGuidedOnboardingRecording(false);
            }
          }

          // A Settings skip ends with no summary, so there is nothing to announce
          if (summaryStatus !== 'skipped') {
            notifySummaryReady(meetingId);
          }

          options?.onFinish?.();
        }
      },
      { fireImmediately: true }
    );
    if (shouldDisposeAfterInit) {
      disposer();
    }
  },

  /**
   * Start listening to the post-start native events once the meeting is started or detected
   * in the background
   * */
  listenToPostStartNativeEvents(): () => void {
    const unlistenAudio = meetingApis.listenAudioLevelsReceived(
      meetingStore.setEnergyLevel
    );
    const unlistenFocus = meetingApis.listenMeetingWindowFocusEvent();
    const unlistenStopNote = meetingApis.listenRequestStopNote();
    return () => {
      unlistenAudio();
      unlistenFocus();
      unlistenStopNote();
    };
  },

  listenAudioLevelsReceived(
    onEnergyLevel: (levels: MeetingAudioEnergyLevel) => void
  ) {
    if (!isCapable(AppCapability.RecordMeeting)) return () => null;

    const unlistenFn = electronApis.meetings.listenAudioEnergyLevelsReceived(
      onSuccess,
      onError
    );

    function onSuccess(energyLevel: MeetingAudioEnergyLevelNative) {
      onEnergyLevel(energyLevel.latestLevels);
    }

    function onError(error: Error) {
      console.error('Failed to listen for audio levels received:', error);
      errorToast(error);
    }

    return unlistenFn;
  },

  listenOpenMeetingChat() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) return () => null;

    return electronApis.meetings.listenOpenMeetingChat((meetingId) => {
      contextChatStore.openForMeeting(meetingId);
      navigate({ to: `/meetings/${meetingId}` });
    });
  },

  listenRequestPrepCalendarEvent() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) {
      return () => null;
    }

    return electronApis.meetings.listenRequestPrepCalendarEvent(
      async (payload) => {
        const existingMeeting = getMeetingByEventId(payload.eventId);
        const meetingId = await ensurePrepMeetingForCalendarEvent(
          { id: payload.eventId, summary: payload.title },
          existingMeeting ? { id: existingMeeting.id } : undefined,
          { onError: () => navigate({ to: '/' }) }
        );
        navigate({
          to: `/meetings/${meetingId}`,
          search: { tab: 'notes' },
        });
      }
    );
  },

  listenRequestStartCalendarEvent() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) {
      return () => null;
    }

    return electronApis.meetings.listenRequestStartCalendarEvent(
      async (payload) => {
        const recording = meetingStore.recording;
        const existingMeeting = getMeetingByEventId(payload.eventId);
        const activeMeetingId =
          recording.status === 'idle' ? undefined : recording.meetingId;

        // Already recording this calendar event's meeting → just open it
        if (
          existingMeeting &&
          activeMeetingId &&
          activeMeetingId === existingMeeting.id
        ) {
          navigate({
            to: `/meetings/${existingMeeting.id}`,
            search: { tab: 'notes' },
          });
          return;
        }

        try {
          await meetingApis.enqueueStartMeeting({
            payload: {
              startReason: 'calendar_event',
              eventId: payload.eventId,
              title: payload.title,
            },
          });
        } catch (error) {
          logError(error, 'unable to handle calendar event start request');
        }
      }
    );
  },

  listenMeetingWindowFocusEvent() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) {
      return () => null;
    }

    return electronApis.meetings.listenMainWindowFocusChange(onChange);

    function onChange({ isFocused }: MeetingWindowFocusChange) {
      const activeMeetingId =
        meetingStore.asRecordingOrStoppingMeeting?.meetingId;
      if (!isFocused && activeMeetingId)
        electronApis.meetings.showMeetingStatusWindow(activeMeetingId);
    }
  },

  listenRequestStopNote() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) {
      return () => null;
    }

    const unlistenFn = electronApis.meetings.listenRequestStopNote(
      async (payload) => {
        await meetingApis.enqueueStopMeeting(
          { type: 'stop_note_taking' },
          { deferSummary: payload?.source === 'user' }
        );
      }
    );

    return unlistenFn;
  },

  async recoverInterruptedLocalPostProcessingRecordings() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) return;

    // The ContextKit fetch stays outside the transition queue so a focus-time
    // scan never delays a user-initiated start; only the state write is queued.
    let payload: MeetingRecoverablePostProcessingRecordingsPayload;
    try {
      payload =
        await electronApis.meetings.getRecoverablePostProcessingRecordings();
    } catch (error) {
      console.warn(
        '[meetings] Failed to recover interrupted post-processing recordings:',
        error
      );
      return;
    }
    if (payload.recordings.length === 0) return;

    return transitionQueue.enqueue({
      type: 'recoverInterrupted',
      recordings: payload.recordings,
    });
  },

  async promoteInterruptedMeeting(meetingId: string) {
    return transitionQueue.enqueue({ type: 'promoteInterrupted', meetingId });
  },

  listenRequestQuickNote() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) {
      return () => null;
    }

    const unlistenFn = electronApis.meetings.listenRequestQuickNote(
      async () => {
        const recording = meetingStore.recording;

        switch (recording.status) {
          case 'idle':
          case 'recording':
            try {
              await meetingApis.enqueueStartMeeting({
                payload: { startReason: 'manual' },
              });
            } catch (error) {
              logError(error, 'unable to handle quick note request');
            }
            return;
          case 'loading':
          case 'stopping':
            return;
          default:
            assertNever(recording);
        }
      }
    );

    return unlistenFn;
  },

  listenRequestStartNote() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) {
      return () => null;
    }

    const unlistenFn = electronApis.meetings.listenRequestStartNote(
      async (payload) => {
        if (payload.startReason === 'resume') {
          const resumeMeetingId = payload.meetingId;

          const shouldIgnoreResume = () => {
            const recording = meetingStore.recording;
            if (recording.status === 'idle') return false;

            const activeMeetingId = recording.meetingId;
            if (!activeMeetingId) return false;
            if (activeMeetingId === resumeMeetingId) return true;

            console.warn(
              '[meetings] Ignored stale resume request while another meeting is active',
              {
                activeMeetingId,
                resumeMeetingId,
              }
            );
            return true;
          };

          // If resume target is already active/loading, skip cleanup and start
          if (shouldIgnoreResume()) return;

          try {
            await electronApis.lbNotifications.removeQueuedById(
              getMeetingSummaryNotificationId(resumeMeetingId)
            );
          } catch (error) {
            logError(
              error,
              '[meetings] Failed to clear queued summary notification during resume'
            );
          }

          if (shouldIgnoreResume()) return;
        }

        try {
          await meetingApis.enqueueStartMeeting({ payload });
        } catch (error) {
          logError(error, 'unable to start meeting');
        }
      }
    );

    return unlistenFn;
  },

  isMeetingBeingRecorded(meetingId: MeetingId): boolean {
    return meetingStore.asRecordingOrStoppingMeeting?.meetingId === meetingId;
  },

  async generateSummary(meetingId: MeetingId): Promise<boolean> {
    try {
      await getStores().meetings.summarizeMeeting(meetingId);

      navigateToSummaryTabIfInView(meetingId);

      meetingApis.pollForSummaryToFinish(meetingId, 'generate_button');
      return true;
    } catch (error) {
      console.error('Failed to generate meeting summary:', error);
      summaryErrorToast("Couldn't generate your meeting summary", () => {
        void meetingApis.generateSummary(meetingId);
      });
      return false;
    }
  },

  async resummarizeMeeting(meetingId: MeetingId): Promise<boolean> {
    const meeting = getMeeting(meetingId);

    if (!meeting) throw new Error('meeting not found');

    const previousStatus = meeting.summary_status;

    try {
      updateStoreMeeting(meetingId, {
        ...meeting,
        summary_status: 'resummarizing',
      });

      await getStores().meetings.summarizeMeeting(meetingId);

      meetingApis.pollForSummaryToFinish(meetingId, 'manual');
      return true;
    } catch (error) {
      updateStoreMeeting(meetingId, {
        ...meeting,
        summary_status: previousStatus,
      });
      console.error('Failed to resummarize meeting:', error);
      summaryErrorToast("Couldn't generate your meeting summary", () => {
        void meetingApis.resummarizeMeeting(meetingId);
      });
      return false;
    }
  },

  /** Get current macOS permissions */
  async getMacPermissions(options?: {
    silent?: boolean;
    throwOnError?: boolean;
  }) {
    if (inBrowser()) return { mic: false } as MeetingPermissions;

    try {
      const permissions = await electronApis.meetings.getMacPermissions();

      if (!permissions) throw new Error('unable to get permissions');

      meetingStore.setMacPermissions(permissions);
      return permissions;
    } catch (error) {
      console.error('Failed to get macOS permissions:', error);
      if (!options?.silent) {
        errorToast(error as Error);
      }
      if (options?.throwOnError) {
        throw error;
      }
      return meetingStore.macPermissions;
    }
  },

  /** Request specific macOS permissions */
  async requestMacPermissions(permissions: MeetingPermissionType[]) {
    try {
      const updatedPermissions =
        await electronApis.meetings.requestMacPermissions(permissions);
      meetingStore.setMacPermissions(updatedPermissions);
      return updatedPermissions;
    } catch (error) {
      console.error('Failed to request macOS permissions:', error);
      errorToast(error as Error);
      return meetingStore.macPermissions;
    }
  },

  /** Merge multiple meetings into one, skipping any currently recording */
  async mergeMeetingNotes(meetingIds: MeetingId[]): Promise<string | null> {
    const mergeable = meetingIds.filter(
      (id) => !meetingApis.isMeetingBeingRecorded(id)
    );
    if (mergeable.length < 2) return null;

    try {
      const { meeting, deleted_meeting_ids } =
        await backendApis.meetingNotes.mergeMeetings(mergeable);

      for (const id of deleted_meeting_ids) {
        removeManager(id);
        meetingStore.transcripts.delete(id);
        meetingStore.removeMeeting(id);
      }

      updateStoreMeeting(meeting.id, meeting);

      queryClient.invalidateQueries({ queryKey: ['meetings'] });

      return meeting.id;
    } catch (error) {
      errorToast(error as Error);
      return null;
    }
  },

  /** Delete multiple meeting notes in parallel, skipping the currently recording meeting */
  async deleteMeetingNotes(meetingIds: MeetingId[]) {
    const deletable = meetingIds.filter(
      (id) => !meetingApis.isMeetingBeingRecorded(id)
    );
    await Promise.all(deletable.map((id) => this.deleteMeetingNote(id)));
  },

  /** Delete meeting note */
  async deleteMeetingNote(meetingId: MeetingId) {
    if (meetingApis.isMeetingBeingRecorded(meetingId)) {
      console.warn(
        '[deleteMeetingNote] Cannot delete meeting that is currently recording'
      );
      return;
    }

    try {
      await getStores().meetings.deleteMeeting(meetingId);
      removeManager(meetingId);
      meetingStore.transcripts.delete(meetingId);
      meetingStore.sidebarPanels.delete(meetingId);
    } catch (error) {
      errorToast(error as Error);
    } finally {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    }
  },

  async getSharedMeetingSummary(
    meetingId: MeetingId,
    publicToken?: string,
    useAuth = false
  ) {
    return await backendApis.meetingNotes.getSharedSummary(
      meetingId,
      publicToken,
      useAuth
    );
  },

  async setMeetingAsPublic(
    meetingId: MeetingId,
    visibility: 'team' | 'public' | 'people' = 'public'
  ) {
    return await getStores().meetings.setMeetingAsPublic(meetingId, visibility);
  },

  async revokeMeetingShare(meetingId: MeetingId) {
    return await getStores().meetings.revokeMeetingShare(meetingId);
  },

  async getMeetingShareStatus(meetingId: MeetingId) {
    return await getStores().meetings.getMeetingShareStatus(meetingId);
  },

  async addMeetingShareInvite(meetingId: MeetingId, email: string) {
    return await getStores().meetings.addMeetingShareInvite(meetingId, email);
  },

  async removeMeetingShareInvite(meetingId: MeetingId, email: string) {
    return await getStores().meetings.removeMeetingShareInvite(
      meetingId,
      email
    );
  },

  /**
   * Subscribe to meeting property changes and notify status window
   * Watches for name and notes changes and deletions, sends patch data directly
   * Note: New meetings are fetched from backend when meeting stops
   */
  subscribeMeetingPropertyChanges() {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) return () => null;

    return meetingStore.subscribeMeetingPropertyChanges(
      ['name', 'notes'],
      (patches) => {
        patches.forEach((patch) => {
          electronApis.meetings.notifyMeetingStatusUpdate({
            type: 'patch_ongoing',
            patch,
          });
        });
      }
    );
  },

  async listenStatusWindowStateChanges() {
    if (!isCapable(AppCapability.MainWindowExclusive)) return () => null;

    return electronApis.meetings.listenMeetingStateDidChange((updates) => {
      meetingStore.statusWindowSyncCount += 1;

      if (updates && Array.isArray(updates)) {
        updates.forEach((patch) => {
          if (patch.deleted) {
            meetingStore.removeMeeting(patch.id);
          } else {
            updateStoreMeeting(patch.id, patch as Partial<Meeting>);
            meetingApis.updateMeeting(
              patch as Partial<Meeting> & { id: MeetingId }
            );
          }
        });
      }
    });
  },

  /** Silently delete empty meetings (no notes, summary, or transcript) */
  async cleanupEmptyMeetings() {
    if (!hasSDK()) return;
    const store = getStores().meetings;
    const recordingId = meetingStore.asRecordingOrStoppingMeeting?.meetingId;
    const emptyIds = getEmptyMeetingIds(store.sortedMeetings, recordingId);
    if (emptyIds.length === 0) return;
    await this.deleteMeetingNotes(emptyIds);
  },

  /**
   * Setup all meeting listeners for main window
   * Groups navigation, start requests, and property change subscriptions
   */
  setupMainWindowListeners() {
    if (!isCapable(AppCapability.MainWindowExclusive)) {
      return () => null;
    }

    this.listenOpenMeetingChat();
    this.listenRequestQuickNote();
    this.listenRequestPrepCalendarEvent();
    this.listenRequestStartCalendarEvent();
    this.listenRequestStartNote();
    setOnPostProcessingFinalized((meetingId, summaryGenerated) => {
      if (summaryGenerated) {
        meetingApis.pollForSummaryToFinish(meetingId, 'auto');
      } else {
        notifyDeferredSummaryReady(meetingId);
      }
    });
    const disposeInterruptedPromotionTimers = armInterruptedPromotionTimers();
    const disposeRecordingMeetingMemory = reaction(
      () => {
        const recording = meetingStore.recording;
        if (recording.status === 'recording') return recording.meetingId;
        return recording.status === 'idle' ? null : undefined;
      },
      (meetingId) => {
        if (meetingId !== undefined) rememberRecordingMeeting(meetingId);
      }
    );
    const disposeMeetingRoutePreload = reaction(
      () => meetingStore.asRecordingOrStoppingMeeting?.meetingId,
      (meetingId) => {
        if (!meetingId) return;
        void preloadRoute({ to: `/meetings/${meetingId}` })?.catch((error) => {
          logError(error, 'Failed to preload meeting route', {
            context: 'setupMainWindowListeners:preloadRoute',
          });
        });
      },
      { fireImmediately: true }
    );
    const reconcileOnFocus = () => {
      void reconcilePostProcessingJobs();
      void this.recoverInterruptedLocalPostProcessingRecordings();
    };
    const reconcileOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        reconcileOnFocus();
      }
    };
    reconcileOnFocus();
    // The mount-time reconcile races SDK store hydration (getMeeting() returns
    // undefined pre-hydration and the scan skips the recording), and no focus
    // event re-fires for a user who launches the app and stays in it. Re-run
    // once hydration completes. Gate on the user too: on a fresh login the
    // store can hydrate before userStore.userId resolves from the network,
    // and a reconcile without a user is a no-op (the jobs ledger is
    // user-filtered), which would waste the only automatic re-run.
    const disposeHydrationReconcile = when(
      () => userStore.userId !== undefined && getStores().meetings.isLoaded,
      reconcileOnFocus
    );
    window.addEventListener('focus', reconcileOnFocus);
    window.addEventListener('online', reconcileOnFocus);
    const releaseRetryTimer = window.setInterval(() => {
      const hasPendingRelease = Array.from(
        meetingStore.postProcessingJobs.values()
      ).some((job) => job.state === 'running' && job.releasePending);
      if (hasPendingRelease) void reconcilePostProcessingJobs();
    }, 60_000);
    document.addEventListener('visibilitychange', reconcileOnVisibility);
    const unsubscribePropertyChanges = this.subscribeMeetingPropertyChanges();
    let unlistenStateChange: (() => void) | undefined;

    this.listenStatusWindowStateChanges().then((unlisten) => {
      unlistenStateChange = unlisten;
    });

    // Re-send the active meeting to the MSW when it requests a resend.
    // This handles the case where the MSW missed the initial meetingStarted
    // broadcast because the panel didn't exist yet. The main window is the
    // source of truth — it checks whether a meeting is actively recording
    // and sends the full meeting through the existing meetingStateDidChange
    // IPC channel.
    const handleActiveMeetingResend = () => {
      const meetingId = meetingStore.asRecordingOrStoppingMeeting?.meetingId;
      if (!meetingId) return;
      const activeTranscriptionEngine =
        meetingStore.recording.status === 'loading' ||
        meetingStore.recording.status === 'recording'
          ? meetingStore.recording.activeTranscriptionEngine
          : undefined;
      const meeting = storeMeetingToMeeting(
        getStores().meetings.get(meetingId)
      );
      if (meeting) {
        electronApis.meetings.notifyMeetingStatusUpdate({
          type: 'ongoing',
          meeting,
          activeTranscriptionEngine,
        });
      }
    };
    const unsubscribeActiveMeetingResend = window.ipcRenderer.on(
      'request-active-meeting-resend',
      handleActiveMeetingResend
    );

    return () => {
      electronApis.meetings.removeListener(IPCEventType.meetingOpenChat);
      electronApis.meetings.removeListener(
        IPCEventType.meetingRequestQuickNote
      );
      electronApis.meetings.removeListener(
        IPCEventType.meetingRequestPrepCalendarEvent
      );
      electronApis.meetings.removeListener(
        IPCEventType.meetingRequestStartCalendarEvent
      );
      electronApis.meetings.removeListener(
        IPCEventType.meetingRequestStartNote
      );
      window.removeEventListener('focus', reconcileOnFocus);
      window.removeEventListener('online', reconcileOnFocus);
      window.clearInterval(releaseRetryTimer);
      document.removeEventListener('visibilitychange', reconcileOnVisibility);
      disposeHydrationReconcile();
      disposeMeetingRoutePreload();
      disposeRecordingMeetingMemory();
      disposeInterruptedPromotionTimers();
      for (const timer of interruptedPromotionTimers.values()) {
        clearTimeout(timer);
      }
      interruptedPromotionTimers.clear();
      unsubscribePropertyChanges?.();
      unlistenStateChange?.();
      unsubscribeActiveMeetingResend();
    };
  },

  enqueueStartMeeting({
    payload,
    onMeetingStarted,
  }: {
    payload: StartMeetingInput;
    onMeetingStarted?: (meetingId: string) => void;
  }) {
    return transitionQueue.enqueue({
      type: 'start',
      payload,
      onMeetingStarted,
    });
  },

  enqueueStopMeeting(
    reason: StopMeetingPayload = { type: 'stop_note_taking' },
    options?: StopMeetingOptions
  ) {
    const isHandStop = reason.type === 'stop_note_taking';
    if (isHandStop) {
      withdrawMeetingAppOffer(meetingAppDetections.stopOfferingDetectedApps());
    }
    return transitionQueue.enqueue({ type: 'stop', reason, options });
  },
};

const transitionQueue = new MeetingTransitionQueue(async (t) => {
  switch (t.type) {
    case 'start':
      return await performStartMeetingTransition({
        payload: t.payload,
        onMeetingStarted: t.onMeetingStarted,
      });
    case 'stop':
      return await performStopMeetingTransition(t.reason, t.options);
    case 'recoverInterrupted':
      return await performRecoverInterruptedTransition(t.recordings);
    case 'promoteInterrupted':
      return await performPromoteInterruptedTransition(t.meetingId);
    default:
      assertNever(t);
  }
});

// The recovery module's promotion timers must go through this queue too (see
// the runtime-invariant comment on performStartMeetingTransition); the enqueue
// is injected so meetings.recovery.ts never imports the queue back.
setPromoteInterruptedEnqueue((meetingId) =>
  meetingApis.promoteInterruptedMeeting(meetingId)
);

// One debouncer and merge buffer per meeting id: meetings save independently,
// and same-meeting field updates merge so a settled entry covers every field
// sent
const meetingUpdateDebouncers = new Map<
  MeetingId,
  ReturnType<typeof debounce>
>();
const pendingMeetingUpdates = new Map<
  MeetingId,
  Partial<Meeting> & { id?: MeetingId }
>();

function clearSavedMeetingUpdate(
  meetingId: MeetingId,
  update: Partial<Meeting> & { id?: MeetingId }
): void {
  const pending = pendingMeetingUpdates.get(meetingId);
  if (!pending) return;

  const remaining = { ...pending };
  for (const field of Object.keys(update) as (keyof Meeting)[]) {
    if (field !== 'id' && remaining[field] === update[field]) {
      delete remaining[field];
    }
  }

  if (Object.keys(remaining).length <= 1) {
    pendingMeetingUpdates.delete(meetingId);
  } else {
    pendingMeetingUpdates.set(meetingId, remaining);
  }
}

function toSdkMeetingUpdate({
  source_app,
  ...otherFields
}: Partial<Meeting>): Partial<SDKMeeting> {
  return {
    ...otherFields,
    ...(source_app !== undefined && { sourceApp: source_app }),
  } as Partial<SDKMeeting>;
}

function debouncedUpdateMeeting(
  update: Partial<Meeting> & { id?: MeetingId }
): void {
  const meetingId = update.id;
  if (!meetingId) return;

  const pending = pendingMeetingUpdates.get(meetingId) ?? {};
  const merged = { ...pending, ...update };
  pendingMeetingUpdates.set(meetingId, merged);

  // Scheduling at merge time keeps the pending save a visible blocker for
  // the whole debounce window
  const saveKey = `meeting:${meetingId}`;
  meetingSaves.schedule(
    saveKey,
    meetingSaves.createRetry(saveKey, async () => {
      await getStores().meetings.updateMeeting(
        meetingId,
        toSdkMeetingUpdate(merged)
      );
      clearSavedMeetingUpdate(meetingId, merged);
    })
  );

  let debouncer = meetingUpdateDebouncers.get(meetingId);
  if (!debouncer) {
    debouncer = debounce(
      () => void flushMeetingUpdate(meetingId),
      UPDATE_DEBOUNCE_MS
    );
    meetingUpdateDebouncers.set(meetingId, debouncer);
  }
  debouncer();
}

async function flushMeetingUpdate(meetingId: MeetingId): Promise<void> {
  const update = pendingMeetingUpdates.get(meetingId);
  if (!update) return;

  const meeting = getMeeting(meetingId);
  const isNameUpdate = update.name && update.name !== meeting?.name;
  const isNotesUpdate = update.notes !== undefined;

  // The merged save entry scheduled at merge time is the one to settle. A
  // stale banner retry can settle it inside the debounce window, so the
  // flush schedules a fresh entry for the flushed payload then
  const saveKey = `meeting:${meetingId}`;
  const activeEntry =
    meetingSaves.getEntry(saveKey) ??
    meetingSaves.schedule(
      saveKey,
      meetingSaves.createRetry(saveKey, async () => {
        await getStores().meetings.updateMeeting(
          meetingId,
          toSdkMeetingUpdate(update)
        );
        clearSavedMeetingUpdate(meetingId, update);
      })
    );

  try {
    await getStores().meetings.updateMeeting(
      meetingId,
      toSdkMeetingUpdate(update)
    );
    clearSavedMeetingUpdate(meetingId, update);
    meetingSaves.settle(activeEntry);

    if (isNameUpdate) {
      trackEvent('MEETING_NAME_UPDATE', {
        meeting_id: meetingId,
        meeting_name: update.name,
      });
    }

    if (isNotesUpdate) {
      trackEvent('MEETING_NOTE_EDITED', { meeting_id: meetingId });
    }
  } catch (error) {
    meetingSaves.fail(activeEntry, error);
    // Put the failed payload back under any newer fields, so the next save
    // sends it too. Without this, a later edit settles a save that never
    // carried these fields.
    pendingMeetingUpdates.set(meetingId, {
      ...update,
      ...(pendingMeetingUpdates.get(meetingId) ?? {}),
    });
    const message = `Failed to update meeting: ${(error as Error).message}`;

    console.error(message, error);
  }
}

const meetingSummaryDebouncers = new Map<
  MeetingId,
  ReturnType<typeof debounce>
>();
// ponytail: one debouncer per edited meeting, never pruned; delete after
// flush if sessions edit thousands of meetings
const pendingMeetingSummaries = new Map<MeetingId, string>();

function debouncedUpdateMeetingSummary({
  id,
  summary,
}: {
  id: MeetingId;
  summary: string;
}): void {
  pendingMeetingSummaries.set(id, summary);

  // Scheduling at call time keeps the pending save a visible blocker for
  // the whole debounce window
  const saveKey = `meeting-summary:${id}`;
  meetingSaves.schedule(
    saveKey,
    meetingSaves.createRetry(saveKey, () =>
      getStores().meetings.updateMeetingSummary(id, summary)
    )
  );

  let debouncer = meetingSummaryDebouncers.get(id);
  if (!debouncer) {
    debouncer = debounce(() => void flushMeetingSummary(id), 250);
    meetingSummaryDebouncers.set(id, debouncer);
  }
  debouncer();
}

async function flushMeetingSummary(id: MeetingId): Promise<void> {
  const summary = pendingMeetingSummaries.get(id);
  pendingMeetingSummaries.delete(id);
  if (summary === undefined) return;

  const toastId = `meeting-summary-save-error-${id}`;
  // A stale toast retry can settle the live entry inside the debounce
  // window; the flush schedules a fresh entry for the flushed summary then
  const saveKey = `meeting-summary:${id}`;
  const activeEntry =
    meetingSaves.getEntry(saveKey) ??
    meetingSaves.schedule(
      saveKey,
      meetingSaves.createRetry(saveKey, () =>
        getStores().meetings.updateMeetingSummary(id, summary)
      )
    );
  try {
    await getStores().meetings.updateMeetingSummary(id, summary);
    meetingSaves.settle(activeEntry);
    toast.dismiss(toastId);
  } catch (error) {
    meetingSaves.fail(activeEntry, error);
    console.error('Failed to update meeting summary:', error);
    summaryErrorToast(
      "Couldn't save your summary changes",
      () => activeEntry.retry(),
      toastId
    );
  }
}

export { debouncedUpdateMeeting, debouncedUpdateMeetingSummary };

// --- Smart Meeting Resolution ---
//
// Data flow:
//   ContextKit detects audio on a meeting app (MeetingAppDetector)
//   → sends appBundleId over IPC
//   → findActiveCalendarEvent(appBundleId)
//       1. Reads cached calendar events from TanStack Query
//       2. Filters to events within the time window (pre-start buffer … post-end buffer)
//       3. Sorts candidates by priority (provider match > in-progress > proximity)
//       4. Returns the best match to start note-taking against

export const PRE_START_BUFFER_MS = 10 * 60 * 1000;
export const SHORT_MEETING_POST_END_BUFFER_MS = 5 * 60 * 1000;
export const LONG_MEETING_POST_END_BUFFER_MS = 10 * 60 * 1000;
export const SHORT_MEETING_THRESHOLD_MS = 45 * 60 * 1000;
/** A calendar event that started longer ago than this is not the call happening
 * now. Multi-day events stay inside the in-progress window for days, and
 * earliest-start ranking would attach today's detection to that old event. */
export const MAX_AUTO_DETECT_EVENT_START_AGE_MS = 12 * 60 * 60 * 1000;
/** Detection fires as call audio begins, so an event in progress for longer than this ranks below one starting near detection */
export const RECENT_START_WINDOW_MS = 30 * 60 * 1000;

/** Lower ranks first. A fixed order keeps the sort consistent: any rule letting
 * an ended event beat an upcoming one forms a cycle with the other two orderings */
const CandidateGroup = {
  RecentInProgress: 0,
  Upcoming: 1,
  LongRunning: 2,
  Ended: 3,
} as const;

function getCandidateGroup(start: number, end: number, now: number) {
  if (end <= now) return CandidateGroup.Ended;
  if (start > now) return CandidateGroup.Upcoming;
  return now - start > RECENT_START_WINDOW_MS
    ? CandidateGroup.LongRunning
    : CandidateGroup.RecentInProgress;
}

function localCalendarDay(date: Date): string {
  return date.toLocaleDateString('en-CA', { timeZone: getDeviceTimeZone() });
}

/** True when this meeting already captured speech on an earlier local calendar
 * day. Prep for this occurrence has no recording timestamps, so it stays
 * eligible. A recording that began yesterday is a different note today, even
 * when the call itself crossed midnight. */
function recordingStartedOnPreviousLocalDay(meeting: Meeting): boolean {
  const timestamps = [
    meeting.first_chunk_timestamp,
    meeting.first_transcript_at,
    meeting.ended_at,
  ].filter((value): value is string => Boolean(value));
  if (!timestamps.length) return false;

  const earliest = timestamps.reduce((oldest, value) =>
    new Date(value).getTime() < new Date(oldest).getTime() ? value : oldest
  );
  const recordedAt = new Date(earliest);
  if (Number.isNaN(recordedAt.getTime())) return false;
  return localCalendarDay(recordedAt) < localCalendarDay(new Date());
}

function getPostEndBuffer(event: CalendarEvent): number {
  const duration =
    new Date(event.end_datetime).getTime() -
    new Date(event.start_datetime).getTime();
  return duration < SHORT_MEETING_THRESHOLD_MS
    ? SHORT_MEETING_POST_END_BUFFER_MS
    : LONG_MEETING_POST_END_BUFFER_MS;
}

/**
 * Meeting-level vocabulary for the transcription start payload. Prefers the
 * backend-computed list; a brand-new calendar meeting races the create POST,
 * so fall back to attendee-name words from the locally cached event.
 */
function resolveStartCustomVocabulary(target: {
  meetingId: string;
  eventId?: string;
}): string[] | undefined {
  const synced = getStores().meetings.get(target.meetingId)?.customVocabulary;
  if (synced != null) return synced.length ? toJS(synced) : undefined;
  if (!target.eventId) return undefined;
  const attendees = findCalendarEventById(target.eventId)?.attendees;
  const words = new Set<string>();
  for (const attendee of attendees ?? []) {
    for (const word of attendee.displayName?.split(/\s+/) ?? []) {
      if (word.length >= 2 && /^\p{L}[\p{L}'-]*\p{L}$/u.test(word))
        words.add(word);
    }
  }
  return words.size ? [...words] : undefined;
}

export function findCalendarEventById(
  eventId: string
): CalendarEvent | undefined {
  const timeZone = getDeviceTimeZone();
  const excludeSoloEvents = config.meetings.exclude_solo_events;
  return queryClient
    .getQueryData<CalendarEvent[]>(
      getCalendarEventsQueryKey(timeZone, excludeSoloEvents)
    )
    ?.find((event) => event.id === eventId);
}

/**
 * Finds the best calendar event to associate with a detected meeting app.
 * A native meeting app never matches an invite that names only other providers.
 *
 * Sort priority:
 *   1. Provider match (appBundleId → provider === event's conference provider)
 *   2. Group: in progress for up to 30 minutes, then upcoming, then in progress
 *      for longer (starts older than 12h are dropped), then ended
 *   3. Within a group: earliest start wins, except among ended events, where
 *      the most recently ended wins
 */
export function findActiveCalendarEvent(
  appBundleId?: string,
  options?: {
    excludeEventId?: string;
    /** The room the caller already knows it is in. A browser bundle id carries
     * no provider, so without this the ranking below falls through to time
     * alone — which cannot separate two concurrent calls on the same provider. */
    identity?: { provider?: MeetingProvider; meetingKey: string };
  }
): CalendarEvent | undefined {
  const timeZone = getDeviceTimeZone();
  const excludeSoloEvents = config.meetings.exclude_solo_events;
  const calendarEvents = queryClient.getQueryData<CalendarEvent[]>(
    getCalendarEventsQueryKey(timeZone, excludeSoloEvents)
  );

  if (!calendarEvents?.length) {
    console.info('[meeting-resolution] no calendar events in cache', {
      appBundleId,
    });
    return undefined;
  }

  const now = Date.now();
  const excludedEventTypes = config.calendar
    .excluded_event_types as EventType[];

  const detectedAppProvider = getProviderFromBundleId(appBundleId);

  const candidates = calendarEvents.filter((event) => {
    if (event.status === 'cancelled') return false;
    if (options?.excludeEventId && event.id === options.excludeEventId)
      return false;
    if (event.eventType && excludedEventTypes.includes(event.eventType))
      return false;
    if (excludeSoloEvents && isSoloEvent(event)) return false;
    if (isDeclinedByUser(event)) return false;

    if (detectedAppProvider) {
      const invitedProviders = getMeetingProviders(event);
      const namesAnotherProvider =
        invitedProviders.length > 0 &&
        !invitedProviders.includes(detectedAppProvider);
      if (namesAnotherProvider) return false;
    }

    const eventStart = new Date(event.start_datetime).getTime();
    const eventEnd = new Date(event.end_datetime).getTime();
    if (now - eventStart > MAX_AUTO_DETECT_EVENT_START_AGE_MS) return false;

    const windowStart = eventStart - PRE_START_BUFFER_MS;
    const windowEnd = eventEnd + getPostEndBuffer(event);
    return windowStart <= now && now <= windowEnd;
  });

  if (!candidates.length) {
    console.info('[meeting-resolution] no calendar events in time window', {
      appBundleId,
      totalEvents: calendarEvents.length,
    });
    return undefined;
  }

  const detectedProvider = detectedAppProvider ?? options?.identity?.provider;

  const roomMatches = options?.identity
    ? candidates.filter((event) => {
        const room = getMeetingRoomFromLink(getMeetingLink(event));
        return (
          room === options.identity?.meetingKey &&
          (!detectedProvider || getMeetingProvider(event) === detectedProvider)
        );
      })
    : [];

  // An exact room match identifies the meeting outright, so anything that does
  // not name the room is out of the running. More than one event can name it —
  // a series duplicated across calendars — and the ranking below then picks
  // between those, never reaching back to an event for a different room.
  const ranked = roomMatches.length > 0 ? roomMatches : candidates;
  if (roomMatches.length > 0) {
    console.info('[meeting-resolution] narrowed candidates by room', {
      appBundleId,
      matches: roomMatches.length,
    });
  }

  const enriched = ranked.map((event) => {
    const start = new Date(event.start_datetime).getTime();
    const end = new Date(event.end_datetime).getTime();
    return {
      event,
      start,
      end,
      group: getCandidateGroup(start, end, now),
      matchesProvider: detectedAppProvider
        ? getMeetingProviders(event).includes(detectedAppProvider)
        : detectedProvider !== undefined &&
          getMeetingProvider(event) === detectedProvider,
    };
  });

  enriched.sort((a, b) => {
    // 1. Provider match
    if (a.matchesProvider && !b.matchesProvider) return -1;
    if (!a.matchesProvider && b.matchesProvider) return 1;

    // 2. Group
    if (a.group !== b.group) return a.group - b.group;

    // 3. Within a group
    return a.group === CandidateGroup.Ended ? b.end - a.end : a.start - b.start;
  });

  const selected = enriched[0].event;
  console.info(
    `[meeting-resolution] found calendar event: ${selected.summary} (id=${selected.id})`,
    { appBundleId, candidates: enriched.length }
  );
  return selected;
}
